import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, onSnapshot, collection, writeBatch, getDocs, Timestamp } from "firebase/firestore";
import { AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// --- IMPORT KOMPONEN ---
import GamesPanel from './GamesPanel'; 
import RekapanDashboard from './Rekapan'; 
import DaftarAgen from './DaftarAgen'; 
import Validator from './Validasi'; 
import LogSpeedPanel from './Speed'; 
import ValidasiTelpon from './ValidasiTelpon'; 
import TelegramUpload from './Telegram'; 
import Broadcast from './Broadcast'; 

// --- CONFIGURATION FIREBASE ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE,
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Helper tanggal WIB (Asia/Jakarta) format YYYY-MM-DD, konsisten di semua timezone perangkat
const getWibDayString = (date = new Date()) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

// --- DEFAULT LOGO, NAME, SUBTITLE & THEME COLORS ---
const DEFAULT_APP_TITLE = "IFYONE";
const DEFAULT_APP_SUBTITLE = "Admin Panel";
const DEFAULT_LOGO = "/logo.png";
const DEFAULT_PRIMARY_COLOR = "#4f46e5";
const DEFAULT_ACCENT_COLOR = "#10b981";
const APP_VERSION = "2.0.0";

// --- TELEGRAM CONFIG ---
const TELEGRAM_TOKEN = "8571888481:AAGdqdiaa4kPd_YWqfZauELZ1-ACiMV0iYg";
const TELEGRAM_CHAT_ID = "-1003574594574";

const WEBHOOK_ID = process.env.REACT_APP_WEBHOOK_ID || "WH-7821-X902";

// --- HELPER VALIDASI WARNA HEX ---
const isValidHexColor = (hex) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
};

// --- HELPER HASHING PASSWORD (SHA-256, tersedia di browser modern) ---
const hashPassword = async (password) => {
    try {
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (err) {
        console.error("Gagal melakukan hashing:", err);
        return null;
    }
};

// --- MODERN UNIFORM SVG ICONS FOR SIDEBAR & NAVBAR ---
const SidebarIcons = {
    dashboard: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
    ),
    speed: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
    ),
    rekapan: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
    ),
    validator: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    ),
    upload: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
    ),
    agen: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    ),
    vtelpon: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
    ),
    games: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
    ),
    telegram: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
    ),
    broadcast: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
    ),
    settings: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    ),
    profile: (
        <svg className="w-4 h-4 shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
    ),
    logout: (
        <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
    )
};

// --- STRUKTUR MENU SIDEBAR (DIKELOMPOKKAN PER KATEGORI) ---
const NAV_GROUPS = [
    {
        title: 'Utama',
        items: [
            { id: 'dashboard', label: 'Dashboard', icon: SidebarIcons.dashboard },
            { id: 'speed', label: 'Monitoring', icon: SidebarIcons.speed }
        ]
    },
    {
        title: 'Data & Laporan',
        items: [
            { id: 'rekapan', label: 'Rekap Operasional', icon: SidebarIcons.rekapan },
            { id: 'upload', label: 'Upload Data', icon: SidebarIcons.upload },
            { id: 'agen', label: 'Daftar Agen', icon: SidebarIcons.agen }
        ]
    },
    {
        title: 'Validasi',
        items: [
            { id: 'v-telpon', label: 'Validasi Paket Telpon', icon: SidebarIcons.vtelpon },
            { id: 'validator', label: 'Validasi Transaksi', icon: SidebarIcons.validator },
            { id: 'games', label: 'Validasi Game', icon: SidebarIcons.games }
        ]
    },
    {
        title: 'Telegram',
        items: [
            { id: 'broadcast', label: 'Broadcast Telegram', icon: SidebarIcons.broadcast },
            { id: 'telegram', label: 'Template Update', icon: SidebarIcons.telegram }
        ]
    },
    {
        title: 'Sistem',
        items: [
            { id: 'setting', label: 'Setting', icon: SidebarIcons.settings }
        ]
    }
];

const formatRP = (val) => {
    const isNegative = val < 0;
    const formatted = new Intl.NumberFormat('id-ID', { 
        style: 'currency', 
        currency: 'IDR', 
        maximumFractionDigits: 0 
    }).format(Math.abs(val) || 0);
    
    if (isNegative) {
        return `-${formatted} (Selisih Harga)`;
    }
    return formatted;
};

const CountUp = ({ value }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const frameRef = useRef();
    const startTimeRef = useRef();
    const duration = 1000; 

    useEffect(() => {
        const end = parseInt(value) || 0;
        const start = displayValue;
        
        if (start === end) return;

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const progress = timestamp - startTimeRef.current;
            const percentage = Math.min(progress / duration, 1);
            
            const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
            const currentCount = Math.floor(start + (end - start) * easeOutQuart);
            
            setDisplayValue(currentCount);

            if (percentage < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };

        startTimeRef.current = null;
        frameRef.current = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(frameRef.current);
    }, [value]);

    return <span>{formatRP(displayValue)}</span>;
};

// --- HELPER & IKON KHUSUS TAMPILAN DASHBOARD ---
const formatCompact = (val) => {
    const n = Number(val) || 0;
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')} M`;
    if (abs >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')} jt`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(0)} rb`;
    return String(n);
};

const DASH_ICON_PATHS = {
    trendUp: <><path d="M22 7 13.5 15.5 8.5 10.5 2 17" /><path d="M16 7h6v6" /></>,
    wallet: <><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></>,
    inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
    percent: <><path d="M19 5 5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>,
    calendar: <><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></>,
    bank: <><path d="m3 9 9-6 9 6" /><path d="M5 9v10" /><path d="M9.5 9v10" /><path d="M14.5 9v10" /><path d="M19 9v10" /><path d="M3 21h18" /></>,
    flame: <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z" />,
    history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5" /><path d="M12 3v12" /></>,
    alert: <><path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
    copy: <><rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>,
    trash: <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
    file: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /></>,
    arrowUp: <><path d="m5 12 7-7 7 7" /><path d="M12 19V5" /></>,
    arrowDown: <><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>,
    shield: <><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></>,
    check: <path d="M20 6 9 17l-5-5" />
};

const DashIcon = ({ name, className = "w-4 h-4" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true">
        {DASH_ICON_PATHS[name]}
    </svg>
);

const DashTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="rounded-xl border border-slate-700 bg-slate-900 px-3.5 py-3 shadow-xl">
            <p className="mb-2 text-[11px] font-semibold text-slate-400">{label}</p>
            <div className="space-y-1.5">
                {payload.map((p) => (
                    <div key={p.dataKey} className="flex items-center justify-between gap-6 text-xs">
                        <span className="flex items-center gap-1.5 text-slate-300">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }}></span>
                            {p.dataKey === 'Jual' ? 'Omset Jual' : p.dataKey}
                        </span>
                        <span className="font-mono font-semibold text-white">{formatRP(p.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [usernameInput, setUsernameInput] = useState("");
    const [passwordInput, setPasswordInput] = useState("");
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); 
    const [isSyncing, setIsSyncing] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    
    // --- STATE FITUR UI/UX & MIKRO INTERAKSI ---
    const [searchHistory, setSearchHistory] = useState("");
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // --- USERNAME / SESSION ---
    const [currentUsername, setCurrentUsername] = useState(() => localStorage.getItem('vpanel_username') || 'admin');
    const [savedCredentials, setSavedCredentials] = useState(null);

    // --- APP NAME STATE ---
    const [appTitle, setAppTitle] = useState(() => localStorage.getItem('ifyone_app_title') || DEFAULT_APP_TITLE);
    const [appTitleInput, setAppTitleInput] = useState(appTitle);

    // --- APP SUBTITLE STATE ---
    const [appSubtitle, setAppSubtitle] = useState(() => localStorage.getItem('ifyone_app_subtitle') || DEFAULT_APP_SUBTITLE);
    const [appSubtitleInput, setAppSubtitleInput] = useState(appSubtitle);

    // --- LOGO STATE ---
    const [currentLogo, setCurrentLogo] = useState(() => localStorage.getItem('ifyone_logo') || DEFAULT_LOGO);
    const [previewLogo, setPreviewLogo] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [logoError, setLogoError] = useState("");
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    // --- THEME COLOR STATE & PREVIEW STATE ---
    const [savedTheme, setSavedTheme] = useState(() => {
        const localPrimary = localStorage.getItem('ifyone_primary_color');
        const localAccent = localStorage.getItem('ifyone_accent_color');
        return {
            primaryColor: isValidHexColor(localPrimary) ? localPrimary : DEFAULT_PRIMARY_COLOR,
            accentColor: isValidHexColor(localAccent) ? localAccent : DEFAULT_ACCENT_COLOR
        };
    });

    const [primaryInput, setPrimaryInput] = useState(savedTheme.primaryColor);
    const [accentInput, setAccentInput] = useState(savedTheme.accentColor);
    const [colorError, setColorError] = useState("");

    // --- PROFILE (USERNAME & PASSWORD) STATE ---
    const [profileUsernameInput, setProfileUsernameInput] = useState("");
    const [profileError, setProfileError] = useState("");
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [currentPasswordInput, setCurrentPasswordInput] = useState("");
    const [newPasswordInput, setNewPasswordInput] = useState("");
    const [confirmPasswordInput, setConfirmPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");
    const [isSavingPassword, setIsSavingPassword] = useState(false);

    // Active Theme yang diterapkan ke CSS Variable UI
    const activeTheme = useMemo(() => {
        return {
            primaryColor: isValidHexColor(primaryInput) ? primaryInput : savedTheme.primaryColor,
            accentColor: isValidHexColor(accentInput) ? accentInput : savedTheme.accentColor
        };
    }, [primaryInput, accentInput, savedTheme]);

    // Sinkronisasi CSS Variable ke Root Dokument
    useEffect(() => {
        document.documentElement.style.setProperty('--primary-color', activeTheme.primaryColor);
        document.documentElement.style.setProperty('--accent-color', activeTheme.accentColor);
    }, [activeTheme]);

    // --- ALARM SALDO SETTINGS STATE (GLOBAL & PER-SUPPLIER) ---
    const [alarmEnabled, setAlarmEnabled] = useState(true);
    const [lowBalanceThreshold, setLowBalanceThreshold] = useState(500000);
    const [supplierAlarmSettings, setSupplierAlarmSettings] = useState({
        DAS: true, AMC: true, HAO: false, NEWBIEZ: true, OASIS: true, ANTUM: true
    });
    const [alertSentStatus, setAlertSentStatus] = useState({
        DAS: false, AMC: false, HAO: false, NEWBIEZ: false, OASIS: false, ANTUM: false
    });

    const AUTH_CREDENTIALS = { 
        user: process.env.REACT_APP_ADMIN_USER || "", 
        pass: process.env.REACT_APP_ADMIN_PASS || "" 
    };

    const [currentTime, setCurrentTime] = useState(new Date());
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [currentPage, setCurrentPage] = useState('dashboard');
    
    const [balances, setBalances] = useState({ DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 });
    const [balanceDates, setBalanceDates] = useState({ DAS: '-', AMC: '-', HAO: '-', NEWBIEZ: '-', OASIS: '-', ANTUM: '-' });
    const [yesterdayBalances, setYesterdayBalances] = useState({ DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 });
    
    const [totals, setTotals] = useState({ jual: 0, modal: 0, profit: 0 });
    const [dailyData, setDailyData] = useState({}); 
    const [supplierDailyUsage, setSupplierDailyUsage] = useState({});
    
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    
    const [topProducts, setTopProducts] = useState([]);
    const [uploadHistory, setUploadHistory] = useState([]);

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    setShowScrollBtn(window.scrollY > 300);
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        if (localStorage.getItem('vpanel_auth') === 'true') setIsLoggedIn(true);

        // Subscriber utama untuk data laporan
        const unsub = onSnapshot(doc(db, "admin_data", "main_report"), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setBalances(data.balances || { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 });
                setBalanceDates(data.balanceDates || { DAS: '-', AMC: '-', HAO: '-', NEWBIEZ: '-', OASIS: '-', ANTUM: '-' });
                setTotals(data.totals || { jual: 0, modal: 0, profit: 0 });
                setDailyData(data.dailyData || {});
                setSupplierDailyUsage(data.supplierDailyUsage || {});
                setYesterdayBalances(data.yesterdayBalances || { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 });
                setTopProducts(data.topProducts || []);
                setUploadHistory(data.uploadHistory || []);
                
                if (data.appTitle) {
                    setAppTitle(data.appTitle);
                    setAppTitleInput(data.appTitle);
                    localStorage.setItem('ifyone_app_title', data.appTitle);
                }
                if (data.appSubtitle) {
                    setAppSubtitle(data.appSubtitle);
                    setAppSubtitleInput(data.appSubtitle);
                    localStorage.setItem('ifyone_app_subtitle', data.appSubtitle);
                }
                
                if (data.appLogo) {
                    setCurrentLogo(data.appLogo);
                    localStorage.setItem('ifyone_logo', data.appLogo);
                }
                if (data.theme) {
                    const pCol = isValidHexColor(data.theme.primaryColor) ? data.theme.primaryColor : DEFAULT_PRIMARY_COLOR;
                    const aCol = isValidHexColor(data.theme.accentColor) ? data.theme.accentColor : DEFAULT_ACCENT_COLOR;
                    setSavedTheme({ primaryColor: pCol, accentColor: aCol });
                    setPrimaryInput(pCol);
                    setAccentInput(aCol);
                    localStorage.setItem('ifyone_primary_color', pCol);
                    localStorage.setItem('ifyone_accent_color', aCol);
                }
                if (data.alarmSettings) {
                    if (data.alarmSettings.enabled !== undefined) setAlarmEnabled(data.alarmSettings.enabled);
                    if (data.alarmSettings.threshold !== undefined) setLowBalanceThreshold(data.alarmSettings.threshold);
                    if (data.alarmSettings.suppliers) {
                        setSupplierAlarmSettings(prev => ({ ...prev, ...data.alarmSettings.suppliers }));
                    }
                }
                if (data.alertSentStatus) {
                    setAlertSentStatus(data.alertSentStatus);
                }
            }
        });

        // Subscriber kredensial custom (username/password override)
        const unsubCreds = onSnapshot(doc(db, "admin_data", "credentials"), (docSnap) => {
            if (docSnap.exists()) {
                const creds = docSnap.data();
                setSavedCredentials(creds);
                if (creds.username) {
                    setCurrentUsername(creds.username);
                    localStorage.setItem('vpanel_username', creds.username);
                }
            }
        });

        return () => {
            clearInterval(timer);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            unsub();
            unsubCreds();
        };
    }, []);

    const sendTelegramAlert = async (supplierName, currentBalance) => {
        const message = `⚠️ *PERINGATAN: SALDO MENIPIS!*\n\nSupplier: *${supplierName}*\nSisa Saldo: *${formatRP(currentBalance)}*\nBatas Minimum: *${formatRP(lowBalanceThreshold)}*\n\n_Mohon segera lakukan deposit ulang._`;
        try {
            await fetch(`https://api.telegram.org/bot${TELEGRAM_TOKEN}/sendMessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chat_id: TELEGRAM_CHAT_ID,
                    text: message,
                    parse_mode: 'Markdown'
                })
            });
        } catch (err) {
            console.error("Gagal mengirim notif Telegram:", err);
        }
    };

    // --- LOGIN HANDLER (Mendukung kredensial Firestore + Fallback ENV) ---
    const handleLogin = async (e) => {
        e.preventDefault();
        const inputUser = usernameInput.trim();
        const inputPass = passwordInput;

        if (!inputUser || !inputPass) {
            showToast("Username dan password wajib diisi!", "error");
            return;
        }

        setIsLoggingIn(true);
        let authenticated = false;
        let resolvedUsername = inputUser;

        // 1) Cek kredensial tersimpan di Firestore
        if (savedCredentials && savedCredentials.username && savedCredentials.passwordHash) {
            if (inputUser === savedCredentials.username) {
                const hash = await hashPassword(inputPass);
                if (hash && hash === savedCredentials.passwordHash) {
                    authenticated = true;
                    resolvedUsername = savedCredentials.username;
                }
            }
        }

        // 2) Fallback ke ENV (jika belum ada override atau jika override tidak cocok)
        if (!authenticated && AUTH_CREDENTIALS.user && AUTH_CREDENTIALS.pass) {
            if (inputUser === AUTH_CREDENTIALS.user && inputPass === AUTH_CREDENTIALS.pass) {
                authenticated = true;
                resolvedUsername = AUTH_CREDENTIALS.user;
            }
        }

        if (authenticated) {
            setIsLoggedIn(true);
            setCurrentUsername(resolvedUsername);
            localStorage.setItem('vpanel_auth', 'true');
            localStorage.setItem('vpanel_username', resolvedUsername);
            setProfileUsernameInput(resolvedUsername);
            setUsernameInput("");
            setPasswordInput("");
            showToast("Berhasil masuk ke sistem access panel", "success");
        } else {
            showToast("Username atau password salah!", "error");
        }
        setIsLoggingIn(false);
    };

    const requestLogout = () => {
        setShowLogoutConfirm(true);
    };

    const confirmLogout = () => {
        setIsLoggedIn(false);
        localStorage.removeItem('vpanel_auth');
        setShowLogoutConfirm(false);
        showToast("Anda telah logout dari sistem.", "success");
    };

    const cancelLogout = () => {
        setShowLogoutConfirm(false);
    };

    const syncToCloud = async (newData) => {
        setIsSyncing(true);
        try {
            await setDoc(doc(db, "admin_data", "main_report"), newData, { merge: true });
            setTimeout(() => setIsSyncing(false), 600);
        } catch (err) {
            console.error("Error Syncing:", err);
            setIsSyncing(false);
            showToast("Gagal sinkronisasi data ke cloud!", "error");
        }
    };

    const handleReset = async () => {
        if(window.confirm("Hapus seluruh data (termasuk riwayat) di Cloud Web Admin?")) {
            const emptyData = {
                balances: { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 },
                balanceDates: { DAS: '-', AMC: '-', HAO: '-', NEWBIEZ: '-', OASIS: '-', ANTUM: '-' },
                totals: { jual: 0, modal: 0, profit: 0 },
                dailyData: {},
                supplierDailyUsage: {},
                yesterdayBalances: { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 },
                topProducts: [],
                uploadHistory: [],
                alertSentStatus: { DAS: false, AMC: false, HAO: false, NEWBIEZ: false, OASIS: false, ANTUM: false }
            };
            
            setBalances(emptyData.balances);
            setBalanceDates(emptyData.balanceDates);
            setTotals(emptyData.totals);
            setDailyData(emptyData.dailyData);
            setSupplierDailyUsage(emptyData.supplierDailyUsage);
            setYesterdayBalances(emptyData.yesterdayBalances);
            setTopProducts([]);
            setUploadHistory([]);
            setAlertSentStatus(emptyData.alertSentStatus);

            await syncToCloud(emptyData);
            localStorage.removeItem('speed_log_data');
            showToast("Seluruh database cloud dan arsip Logspeed berhasil direset!", "success");
        }
    };

    // --- APP NAME HANDLERS ---
    const handleSaveAppName = async () => {
        const cleanName = appTitleInput.trim() || DEFAULT_APP_TITLE;
        if (cleanName.length < 2) {
            showToast("Nama aplikasi minimal 2 karakter!", "error");
            return;
        }
        if (cleanName.length > 32) {
            showToast("Nama aplikasi maksimal 32 karakter!", "error");
            return;
        }
        try {
            setAppTitle(cleanName);
            setAppTitleInput(cleanName);
            localStorage.setItem('ifyone_app_title', cleanName);
            await syncToCloud({ appTitle: cleanName });
            showToast("Nama Aplikasi berhasil diperbarui.", "success");
        } catch (err) {
            console.error("Gagal menyimpan nama aplikasi:", err);
            showToast("Gagal menyimpan nama aplikasi.", "error");
        }
    };

    const handleResetAppName = async () => {
        if (window.confirm("Kembalikan nama aplikasi ke default (IFYONE)?")) {
            try {
                setAppTitle(DEFAULT_APP_TITLE);
                setAppTitleInput(DEFAULT_APP_TITLE);
                localStorage.setItem('ifyone_app_title', DEFAULT_APP_TITLE);
                await syncToCloud({ appTitle: DEFAULT_APP_TITLE });
                showToast("Nama Aplikasi dikembalikan ke default.", "success");
            } catch (err) {
                console.error("Gagal mereset nama aplikasi:", err);
                showToast("Gagal mereset nama aplikasi.", "error");
            }
        }
    };

    // --- SUBTITLE HANDLERS ---
    const handleSaveAppSubtitle = async () => {
        const cleanSubtitle = appSubtitleInput.trim() || DEFAULT_APP_SUBTITLE;
        if (cleanSubtitle.length > 40) {
            showToast("Subtitle maksimal 40 karakter!", "error");
            return;
        }
        try {
            setAppSubtitle(cleanSubtitle);
            setAppSubtitleInput(cleanSubtitle);
            localStorage.setItem('ifyone_app_subtitle', cleanSubtitle);
            await syncToCloud({ appSubtitle: cleanSubtitle });
            showToast("Subtitle aplikasi berhasil diperbarui.", "success");
        } catch (err) {
            console.error("Gagal menyimpan subtitle:", err);
            showToast("Gagal menyimpan subtitle aplikasi.", "error");
        }
    };

    const handleResetAppSubtitle = async () => {
        if (window.confirm("Kembalikan subtitle ke default (Admin Panel)?")) {
            try {
                setAppSubtitle(DEFAULT_APP_SUBTITLE);
                setAppSubtitleInput(DEFAULT_APP_SUBTITLE);
                localStorage.setItem('ifyone_app_subtitle', DEFAULT_APP_SUBTITLE);
                await syncToCloud({ appSubtitle: DEFAULT_APP_SUBTITLE });
                showToast("Subtitle dikembalikan ke default.", "success");
            } catch (err) {
                console.error("Gagal mereset subtitle:", err);
                showToast("Gagal mereset subtitle.", "error");
            }
        }
    };

    // --- COLOR SETTINGS HANDLERS ---
    const handleSaveColors = async () => {
        setColorError("");
        if (!isValidHexColor(primaryInput)) {
            setColorError("Kode Warna Utama tidak valid! Gunakan format HEX seperti #4F46E5");
            return;
        }
        if (!isValidHexColor(accentInput)) {
            setColorError("Kode Warna Aksen tidak valid! Gunakan format HEX seperti #10B981");
            return;
        }

        try {
            const newTheme = {
                primaryColor: primaryInput,
                accentColor: accentInput
            };

            setSavedTheme(newTheme);
            localStorage.setItem('ifyone_primary_color', primaryInput);
            localStorage.setItem('ifyone_accent_color', accentInput);

            await syncToCloud({
                theme: newTheme
            });

            showToast("Pengaturan Warna Tampilan berhasil disimpan secara global.", "success");
        } catch (err) {
            console.error("Gagal menyimpan warna:", err);
            showToast("Gagal menyimpan pengaturan warna ke cloud.", "error");
        }
    };

    const handleResetColors = async () => {
        if (window.confirm("Apakah Anda yakin ingin mengembalikan warna tampilan ke konfigurasi default sistem?")) {
            setColorError("");
            const defaultTheme = {
                primaryColor: DEFAULT_PRIMARY_COLOR,
                accentColor: DEFAULT_ACCENT_COLOR
            };

            setPrimaryInput(DEFAULT_PRIMARY_COLOR);
            setAccentInput(DEFAULT_ACCENT_COLOR);
            setSavedTheme(defaultTheme);

            localStorage.setItem('ifyone_primary_color', DEFAULT_PRIMARY_COLOR);
            localStorage.setItem('ifyone_accent_color', DEFAULT_ACCENT_COLOR);

            try {
                await syncToCloud({
                    theme: defaultTheme
                });
                showToast("Warna berhasil dikembalikan ke default sistem.", "success");
            } catch (err) {
                console.error("Gagal mereset warna:", err);
                showToast("Gagal mereset warna ke cloud.", "error");
            }
        }
    };

    // --- ALARM SETTINGS HANDLERS ---
    const handleSaveAlarmSettings = async () => {
        try {
            await syncToCloud({
                alarmSettings: {
                    enabled: alarmEnabled,
                    threshold: Number(lowBalanceThreshold) || 500000,
                    suppliers: supplierAlarmSettings
                }
            });
            showToast("Pengaturan Alarm Saldo berhasil disimpan.", "success");
        } catch (err) {
            console.error("Gagal menyimpan pengaturan alarm:", err);
            showToast("Gagal menyimpan pengaturan alarm.", "error");
        }
    };

    const toggleSupplierAlarm = (supplierKey) => {
        setSupplierAlarmSettings(prev => ({
            ...prev,
            [supplierKey]: !prev[supplierKey]
        }));
    };

    // --- LOGO SETTINGS HANDLERS ---
    const handleLogoSelect = (e) => {
        const file = e.target.files[0];
        setLogoError("");

        if (!file) return;

        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
            setLogoError("Format file tidak valid. Gunakan PNG, JPG, WebP, atau SVG.");
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setLogoError("Ukuran file terlalu besar. Maksimal 2MB.");
            return;
        }

        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewLogo(reader.result);
        };
        reader.onerror = () => {
            setLogoError("Gagal membaca file gambar.");
        };
        reader.readAsDataURL(file);
    };

    const handleSaveLogo = async () => {
        if (!previewLogo) {
            showToast("Pilih file logo terlebih dahulu!", "error");
            return;
        }

        setIsUploadingLogo(true);
        try {
            setCurrentLogo(previewLogo);
            localStorage.setItem('ifyone_logo', previewLogo);

            await syncToCloud({
                appLogo: previewLogo
            });

            setPreviewLogo(null);
            setSelectedFile(null);
            showToast("Logo berhasil diperbarui.", "success");
        } catch (err) {
            console.error("Gagal mengunggah logo:", err);
            showToast("Logo gagal diperbarui. Silakan coba lagi.", "error");
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleResetLogo = async () => {
        if (window.confirm("Apakah Anda yakin ingin mengembalikan logo ke versi default?")) {
            setIsUploadingLogo(true);
            try {
                setCurrentLogo(DEFAULT_LOGO);
                setPreviewLogo(null);
                setSelectedFile(null);
                setLogoError("");
                localStorage.removeItem('ifyone_logo');

                await syncToCloud({
                    appLogo: DEFAULT_LOGO
                });

                showToast("Logo berhasil dikembalikan ke default.", "success");
            } catch (err) {
                console.error("Gagal mereset logo:", err);
                showToast("Logo gagal diperbarui. Silakan coba lagi.", "error");
            } finally {
                setIsUploadingLogo(false);
            }
        }
    };

    // --- PROFILE HANDLERS (USERNAME + PASSWORD) ---
    const handleSaveUsername = async () => {
        setProfileError("");
        const cleanUser = (profileUsernameInput || '').trim();

        if (!cleanUser) {
            setProfileError("Username tidak boleh kosong.");
            return;
        }
        if (cleanUser.length < 3) {
            setProfileError("Username minimal 3 karakter.");
            return;
        }
        if (cleanUser.length > 30) {
            setProfileError("Username maksimal 30 karakter.");
            return;
        }
        if (!/^[a-zA-Z0-9_.-]+$/.test(cleanUser)) {
            setProfileError("Username hanya boleh huruf, angka, titik, underscore, dan strip.");
            return;
        }

        setIsSavingProfile(true);
        try {
            const newCreds = {
                ...(savedCredentials || {}),
                username: cleanUser,
                updatedAt: Timestamp.now()
            };
            await setDoc(doc(db, "admin_data", "credentials"), newCreds, { merge: true });
            setSavedCredentials(newCreds);
            setCurrentUsername(cleanUser);
            localStorage.setItem('vpanel_username', cleanUser);
            showToast("Username berhasil diperbarui.", "success");
        } catch (err) {
            console.error("Gagal menyimpan username:", err);
            setProfileError("Gagal menyimpan username. Coba lagi.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordError("");
        setPasswordSuccess("");

        if (!currentPasswordInput || !newPasswordInput || !confirmPasswordInput) {
            setPasswordError("Semua kolom password wajib diisi.");
            return;
        }

        if (newPasswordInput.length < 6) {
            setPasswordError("Password baru minimal 6 karakter.");
            return;
        }
        if (newPasswordInput.length > 64) {
            setPasswordError("Password baru maksimal 64 karakter.");
            return;
        }
        if (newPasswordInput !== confirmPasswordInput) {
            setPasswordError("Konfirmasi password baru tidak cocok.");
            return;
        }

        // Verifikasi password saat ini
        let currentValid = false;
        if (savedCredentials && savedCredentials.passwordHash) {
            const hash = await hashPassword(currentPasswordInput);
            currentValid = !!hash && hash === savedCredentials.passwordHash;
        } else if (AUTH_CREDENTIALS.pass) {
            currentValid = currentPasswordInput === AUTH_CREDENTIALS.pass;
        }

        if (!currentValid) {
            setPasswordError("Password saat ini salah.");
            return;
        }

        setIsSavingPassword(true);
        try {
            const newHash = await hashPassword(newPasswordInput);
            if (!newHash) {
                setPasswordError("Gagal memproses password (browser tidak mendukung crypto).");
                setIsSavingPassword(false);
                return;
            }

            const newCreds = {
                ...(savedCredentials || {}),
                username: savedCredentials?.username || currentUsername,
                passwordHash: newHash,
                updatedAt: Timestamp.now()
            };

            await setDoc(doc(db, "admin_data", "credentials"), newCreds, { merge: true });
            setSavedCredentials(newCreds);
            setCurrentPasswordInput("");
            setNewPasswordInput("");
            setConfirmPasswordInput("");
            setPasswordSuccess("Password berhasil diperbarui.");
            showToast("Password berhasil diperbarui.", "success");
        } catch (err) {
            console.error("Gagal menyimpan password:", err);
            setPasswordError("Gagal menyimpan password. Coba lagi.");
        } finally {
            setIsSavingPassword(false);
        }
    };

    const parseNum = (val, isAntum = false) => {
        if (!val) return 0;
        if (typeof val === 'number') return val;
        let clean = String(val).trim();
        if (isAntum) {
            clean = clean.replace(/,/g, "").replace(/\./g, "");
        } else {
            clean = clean.replace(/\./g, "").replace(/,/g, ".");
        }
        return parseFloat(clean) || 0;
    };

    const parseFinalBalance = (text, sKey) => {
        if (!text) return null;
        const strText = String(text);

        if (sKey === 'NEWBIEZ') {
            const newbiezMatch = strText.match(/=\s*([\d\.,]+)\s*@/i);
            if (newbiezMatch) {
                let rawNum = newbiezMatch[1].trim(); 
                rawNum = rawNum.replace(/,/g, '.');
                const parts = rawNum.split('.');
                if (parts.length > 1) {
                    const lastPart = parts[parts.length - 1];
                    if (lastPart.length <= 2) {
                        parts.pop(); 
                    }
                }
                const cleanDigits = parts.join('');
                return parseInt(cleanDigits, 10) || 0;
            }
        }

        if (sKey === 'AMC') {
            const amcMatch = strText.match(/Rp\.?\s*([\d\.,]+)/i);
            if (amcMatch) return parseNum(amcMatch[1]);
        }
        if (sKey === 'OASIS') {
            const oasisMatch = strText.match(/Rp\.?\s*([\d\.]+),/i);
            if (oasisMatch) return parseNum(oasisMatch[1]);
        }
        if (sKey === 'ANTUM') {
            const antumMatch = strText.match(/=\s*([\d\.,]+)/i) || strText.match(/Sal\s*([\d\.,]+)/i);
            if (antumMatch) return parseNum(antumMatch[1], true);
        }
        
        let match = strText.match(/(?:Sal|Saldo|Sisa|Sisa Saldo)\.?\s*Rp\.?\s*([\d\.,]+)/i) || 
                    strText.match(/Rp\.?\s*([\d\.,]+)/i) || 
                    strText.match(/([\d\.,]+)\s*@/);
                    
        return match ? parseNum(match[1]) : null;
    };

    const processFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: false });

            try {
                const rawHeaderJson = XLSX.utils.sheet_to_json(sheet, { defval: "", header: "A" });
                const firstCell = String(rawHeaderJson[0]?.['A'] || '').toLowerCase();
                const speedRows = (firstCell.includes('tgl') || firstCell.includes('entri') || firstCell.includes('tanggal')) ? rawHeaderJson.slice(1) : rawHeaderJson;

                const BATCH_SIZE = 400;
                const uploadDay = getWibDayString();

                const existingSnap = await getDocs(collection(db, "speed_log_data"));
                const staleDocs = existingSnap.docs.filter(d => d.data().uploadDay !== uploadDay);
                for (let i = 0; i < staleDocs.length; i += BATCH_SIZE) {
                    const delBatch = writeBatch(db);
                    staleDocs.slice(i, i + BATCH_SIZE).forEach(d => delBatch.delete(d.ref));
                    await delBatch.commit();
                }

                for (let i = 0; i < speedRows.length; i += BATCH_SIZE) {
                    const chunk = speedRows.slice(i, i + BATCH_SIZE);
                    const batch = writeBatch(db);
                    chunk.forEach((row) => {
                        const rowRef = doc(collection(db, "speed_log_data"));
                        batch.set(rowRef, { 
                            ...row, 
                            rawDate: new Date().toISOString(),
                            uploadDay: uploadDay,
                            created_at: Timestamp.now()
                        });
                    });
                    await batch.commit();
                }
            } catch (err) {
                console.error("Gagal sinkronisasi otomatis ke Log Speed Firestore:", err);
            }

            let tempYesterday = { ...balances }; 
            let tempBalances = { ...balances };
            let tempDates = { ...balanceDates };
            let tempDaily = { ...dailyData };
            let tempUsage = { ...supplierDailyUsage };
            let tempAlertStatus = { ...alertSentStatus };
            let lastUpdateTimes = { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 };
            let newAccJual = totals.jual;
            let newAccModal = totals.modal;
            let productCounts = {};

            let countSukses = 0;
            let countGagal = 0;

            let fileStats = {
                fileName: file.name,
                uploadTime: new Date().toLocaleString('id-ID'),
                id: Date.now(),
                totalJual: 0,
                totalModal: 0,
                trxSukses: 0,
                trxGagal: 0
            };

            jsonData.forEach(row => {
                const getVal = (kws) => {
                    const key = Object.keys(row).find(k => kws.some(kw => k.trim().toLowerCase() === kw.toLowerCase()));
                    return row[key] ? String(row[key]).trim() : "";
                };
                
                const statusRaw = (getVal(['Status', 'Keterangan Status', 'sts']) || "").toLowerCase();
                const kolomSVal = (getVal(['S', 'Status Provider']) || "").toLowerCase();
                const msgVal = (getVal(['Message', 'Keterangan', 'SN', 'Ket', 'Berita', 'Jawaban Provider']) || "").toLowerCase();

                const isFail = statusRaw.includes('gagal') || statusRaw.includes('reject') || statusRaw.includes('cancel') || 
                               statusRaw.includes('fail') || statusRaw.includes('batal') || statusRaw.includes('error') ||
                               kolomSVal.includes('gagal') || kolomSVal.includes('reject') || kolomSVal.includes('cancel') || 
                               msgVal.includes('gagal') || msgVal.includes('sisa saldo tidak cukup') || msgVal.includes('saldo tidak cukup');

                const status = isFail ? 'gagal' : 'sukses';

                const kodeProduk = (getVal(['Kode Produk', 'Produk', 'Kode']) || "").toUpperCase();
                const supplierRaw = (getVal(['Nama Supplier', 'Supplier', 'Provider', 'Vendor']) || "UNKNOWN").toUpperCase();
                const message = getVal(['Message', 'Keterangan', 'SN', 'Ket', 'Berita', 'Jawaban Provider']);
                const tglRaw = getVal(['Tanggal', 'Waktu', 'Date', 'Time']);
                const currentRecordTime = new Date(tglRaw).getTime() || Date.now();
                const dateOnly = tglRaw ? tglRaw.split(' ')[0] : new Date().toISOString().split('T')[0];
                const hrgModal = parseNum(getVal(['Harga Modal', 'Modal', 'HPP', 'Harga Beli']));
                const hrgJual = parseNum(getVal(['Harga Jual', 'Harga', 'Jual']));

                let sKey = null;
                if (supplierRaw.includes('DAS')) sKey = 'DAS';
                else if (supplierRaw.includes('AMC')) sKey = 'AMC';
                else if (supplierRaw.includes('HAO')) sKey = 'HAO';
                else if (supplierRaw.includes('NEWBIEZ')) sKey = 'NEWBIEZ';
                else if (supplierRaw.includes('OASIS')) sKey = 'OASIS';
                else if (supplierRaw.includes('ANTUM')) sKey = 'ANTUM';

                if (status === 'sukses') {
                    countSukses++;
                    newAccModal += hrgModal;
                    newAccJual += hrgJual;
                    fileStats.totalJual += hrgJual;
                    fileStats.totalModal += hrgModal;
                    
                    if(kodeProduk) {
                        productCounts[kodeProduk] = (productCounts[kodeProduk] || 0) + 1;
                    }

                    if(!tempDaily[dateOnly]) tempDaily[dateOnly] = { jual: 0, modal: 0, profit: 0 };
                    tempDaily[dateOnly].jual += hrgJual;
                    tempDaily[dateOnly].modal += hrgModal;
                    tempDaily[dateOnly].profit += (hrgJual - hrgModal);
                    
                    if (sKey) {
                        if (!tempUsage[dateOnly]) tempUsage[dateOnly] = { DAS: 0, AMC: 0, HAO: 0, NEWBIEZ: 0, OASIS: 0, ANTUM: 0 };
                        tempUsage[dateOnly][sKey] = (tempUsage[dateOnly][sKey] || 0) + hrgModal;
                    }
                } else if (status === 'gagal') {
                    countGagal++;
                }
                
                if (sKey) {
                    const saldoBaru = parseFinalBalance(message, sKey);
                    if (saldoBaru !== null && currentRecordTime >= (lastUpdateTimes[sKey] || 0)) {
                        tempBalances[sKey] = saldoBaru;
                        tempDates[sKey] = tglRaw || new Date().toLocaleString('id-ID');
                        lastUpdateTimes[sKey] = currentRecordTime;
                    }
                }
            });

            if (alarmEnabled) {
                Object.entries(tempBalances).forEach(([name, bal]) => {
                    const isSupplierAlarmOn = supplierAlarmSettings[name] !== false;
                    if (isSupplierAlarmOn && bal > 0 && bal < lowBalanceThreshold) {
                        if (!tempAlertStatus[name]) {
                            sendTelegramAlert(name, bal);
                            tempAlertStatus[name] = true; 
                        }
                    } else if (bal >= lowBalanceThreshold || !isSupplierAlarmOn) {
                        tempAlertStatus[name] = false; 
                    }
                });
            }

            fileStats.trxSukses = countSukses;
            fileStats.trxGagal = countGagal;

            const sortedProducts = Object.entries(productCounts)
                .map(([kode, qty]) => ({ kode, qty }))
                .sort((a, b) => b.qty - a.qty)
                .slice(0, 5);

            await syncToCloud({
                balances: tempBalances,
                balanceDates: tempDates,
                totals: { jual: newAccJual, modal: newAccModal, profit: newAccJual - newAccModal },
                dailyData: tempDaily,
                yesterdayBalances: tempYesterday,
                supplierDailyUsage: tempUsage,
                topProducts: sortedProducts,
                uploadHistory: [fileStats, ...uploadHistory].slice(0, 10),
                alertSentStatus: tempAlertStatus
            });
            
            showToast(`File diproses! ${countSukses} Sukses & ${countGagal} Gagal. Saldo & Log Speed diperbarui.`, "success");
            setCurrentPage('dashboard');
        };
        reader.readAsArrayBuffer(file);
    };

    const deleteHistoryItem = async (id) => {
        if(window.confirm("Hapus catatan riwayat ini?")) {
            const updatedHistory = uploadHistory.filter(item => item.id !== id);
            await syncToCloud({
                balances,
                balanceDates,
                totals,
                dailyData,
                yesterdayBalances,
                supplierDailyUsage,
                topProducts,
                uploadHistory: updatedHistory
            });
            showToast("Item riwayat berhasil dihapus", "success");
        }
    };

    const rangeStats = useMemo(() => {
        let rangeJual = 0;
        let rangeModal = 0;
        let rangeProfit = 0;

        Object.keys(dailyData).forEach(date => {
            if (date >= startDate && date <= endDate) {
                rangeJual += dailyData[date].jual || 0;
                rangeModal += dailyData[date].modal || 0;
                rangeProfit += dailyData[date].profit || 0;
            }
        });

        return { jual: rangeJual, modal: rangeModal, profit: rangeProfit };
    }, [dailyData, startDate, endDate]);

    const getChartData = (sKey) => {
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            last7Days.push({
                date: d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
                usage: supplierDailyUsage[dateStr]?.[sKey] || 0
            });
        }
        return last7Days;
    };

    const getCombinedTotalChartData = () => {
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            last7Days.push({
                date: d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
                Jual: dailyData[dateStr]?.jual || 0,
                Profit: dailyData[dateStr]?.profit || 0
            });
        }
        return last7Days;
    };

    const setPeriodShortcut = (days) => {
        const end = new Date().toISOString().split('T')[0];
        const startDay = new Date();
        startDay.setDate(startDay.getDate() - days);
        const start = startDay.toISOString().split('T')[0];
        setStartDate(start);
        setEndDate(end);
        showToast(`Filter: ${days === 0 ? 'Hari Ini' : days + 1 + ' Hari Terakhir'}`, "success");
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text);
        showToast(`Disalin: ${text}`, "success");
    };

    const filteredUploadHistory = useMemo(() => {
        return uploadHistory.filter(item => 
            item.fileName.toLowerCase().includes(searchHistory.toLowerCase())
        );
    }, [uploadHistory, searchHistory]);

    const lowBalanceSuppliers = useMemo(() => {
        return Object.entries(balances)
            .filter(([name, bal]) => {
                const isAlarmOn = supplierAlarmSettings[name] !== false;
                return isAlarmOn && bal > 0 && bal < lowBalanceThreshold;
            })
            .map(([name, bal]) => ({ name, bal }));
    }, [balances, lowBalanceThreshold, supplierAlarmSettings]);

    // --- PROFESIONAL MODERN LOGIN VIEW ---
    if (!isLoggedIn) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans relative overflow-hidden">
                <div 
                    style={{ backgroundColor: activeTheme.primaryColor }}
                    className="absolute -top-32 -left-32 w-96 h-96 rounded-full blur-[120px] opacity-30 pointer-events-none"
                ></div>
                <div 
                    style={{ backgroundColor: activeTheme.accentColor }}
                    className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full blur-[120px] opacity-25 pointer-events-none"
                ></div>

                <div className="max-w-md w-full relative z-10">
                    <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/60 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                        
                        <div 
                            style={{ backgroundColor: activeTheme.primaryColor }}
                            className="absolute top-0 left-0 right-0 h-1.5"
                        ></div>

                        <div className="text-center mb-8 pt-2">
                            <div className="w-16 h-16 rounded-2xl bg-slate-900/80 border border-slate-700/80 flex items-center justify-center p-2 mx-auto mb-4 shadow-lg group hover:scale-105 transition-transform">
                                <img 
                                    src={currentLogo} 
                                    alt="App Logo" 
                                    className="w-full h-full object-contain filter drop-shadow" 
                                    onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_LOGO; }} 
                                />
                            </div>
                            
                            <h2 className="text-white font-black text-2xl tracking-tight uppercase">
                                {appTitle}
                            </h2>
                            <p className="text-slate-400 text-xs mt-1 font-medium uppercase tracking-widest">{appSubtitle}</p>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5 pl-1">
                                    Username
                                </label>
                                <input 
                                    type="text" 
                                    placeholder="Masukkan username" 
                                    className="w-full bg-slate-900/70 border border-slate-700/80 rounded-xl py-3 px-4 text-xs font-medium text-slate-100 placeholder-slate-500 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" 
                                    value={usernameInput} 
                                    onChange={(e) => setUsernameInput(e.target.value)} 
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5 pl-1">
                                    Password
                                </label>
                                <input 
                                    type="password" 
                                    placeholder="••••••••" 
                                    className="w-full bg-slate-900/70 border border-slate-700/80 rounded-xl py-3 px-4 text-xs font-medium text-slate-100 placeholder-slate-500 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" 
                                    value={passwordInput} 
                                    onChange={(e) => setPasswordInput(e.target.value)} 
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={isLoggingIn}
                                style={{ backgroundColor: activeTheme.primaryColor }}
                                className="w-full text-white py-3.5 rounded-xl text-xs font-extrabold uppercase tracking-widest transition-all shadow-lg hover:opacity-90 active:scale-[0.99] mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                <span>{isLoggingIn ? 'MEMPROSES...' : 'MASUK KE DASHBOARD'}</span>
                                {!isLoggingIn && <span>→</span>}
                            </button>
                        </form>

                        <div className="mt-8 pt-4 border-t border-slate-700/50 text-center">
                            <span className="text-[10px] font-semibold text-slate-500">
                                Protected System Console • v{APP_VERSION} • {new Date().getFullYear()}
                            </span>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8fafc] flex flex-col md:flex-row font-sans text-xs text-slate-600 antialiased relative">
            
            {toast.show && (
                <div className="fixed top-4 right-4 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl bg-slate-900 text-white transition-all duration-300 border border-slate-700 animate-[toastIn_0.3s_ease]">
                    <span className="text-sm">{toast.type === 'error' ? '❌' : '⚡'}</span>
                    <span className="font-bold tracking-wide text-xs">{toast.message}</span>
                </div>
            )}

            {showScrollBtn && (
                <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    style={{ backgroundColor: activeTheme.primaryColor }}
                    className="fixed bottom-5 right-5 z-50 w-10 h-10 text-white rounded-xl shadow-lg flex items-center justify-center text-sm hover:opacity-90 transition-all"
                >
                    ▲
                </button>
            )}

            {/* MODAL KONFIRMASI LOGOUT */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-[fadeIn_0.2s_ease]">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-sm w-full p-6 animate-[popIn_0.25s_cubic-bezier(0.34,1.56,0.64,1)]">
                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 border border-red-200 mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </div>
                        <h3 className="text-center text-sm font-black text-slate-800 uppercase tracking-wide mb-1">Konfirmasi Logout</h3>
                        <p className="text-center text-[11px] text-slate-500 leading-relaxed mb-5">
                            Apakah Anda yakin ingin keluar dari sesi <strong className="text-slate-700">{currentUsername}</strong>? Anda perlu login kembali untuk mengakses admin panel.
                        </p>
                        <div className="flex gap-2">
                            <button 
                                onClick={cancelLogout}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs transition-colors"
                            >
                                Batal
                            </button>
                            <button 
                                onClick={confirmLogout}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs shadow-md shadow-red-600/20 transition-colors"
                            >
                                Ya, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MOBILE NAVBAR HEADER */}
            <header className="md:hidden bg-white border-b border-slate-200 text-slate-800 px-5 py-3 sticky top-0 z-[60] shadow-sm flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden p-1 shrink-0 shadow-sm">
                        <img 
                            src={currentLogo} 
                            alt={`${appTitle} Logo`} 
                            className="w-full h-full object-contain filter drop-shadow-sm" 
                            onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_LOGO; }} 
                        />
                    </div>
                    <div className="flex flex-col justify-center">
                        <div className="flex items-center gap-1">
                            <span className="font-black text-xs tracking-tight uppercase text-slate-800">{appTitle}</span>
                        </div>
                        <span className="text-[8px] text-slate-400 font-bold tracking-wider uppercase">{appSubtitle}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => setCurrentPage('setting')}
                        style={currentPage === 'setting' ? { color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30` } : {}}
                        className={`p-2 rounded-xl border transition-all ${currentPage === 'setting' ? '' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                        title="Setting"
                    >
                        {SidebarIcons.settings}
                    </button>
                    <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 border border-slate-200 text-sm">
                        {isSidebarOpen ? '✕' : '☰'}
                    </button>
                </div>
            </header>

            {/* DESKTOP SIDEBAR */}
            <aside className={`w-64 bg-white text-slate-700 p-5 fixed h-full flex flex-col z-50 transition-transform duration-300 md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} pt-20 md:pt-6 border-r border-slate-200 shadow-sm`}>
                
                {/* LOGO & TITLE SECTION */}
                <div className="hidden md:flex items-center gap-3 mb-8 px-1">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-50 via-white to-slate-50 border border-slate-200/80 flex items-center justify-center p-1.5 shrink-0 shadow-sm transition-transform hover:scale-105">
                        <img 
                            src={currentLogo} 
                            alt={`${appTitle} Logo`} 
                            className="w-full h-full object-contain filter drop-shadow-sm" 
                            onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_LOGO; }} 
                        />
                    </div>
                    <div className="flex flex-col justify-center min-w-0">
                        <div className="flex items-center gap-1.5">
                            <h2 className="text-sm font-black tracking-tight uppercase text-slate-800 truncate">{appTitle}</h2>
                        </div>
                        <span className="text-[9px] font-bold text-slate-400 tracking-wider uppercase -mt-0.5 truncate">{appSubtitle}</span>
                    </div>
                </div>

                <nav className="flex-grow overflow-y-auto pr-1 pb-2">
                    {NAV_GROUPS.map((group, gIdx) => (
                        <div key={group.title} className={gIdx === 0 ? '' : 'mt-4'}>
                            <p className="px-4 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 select-none">
                                {group.title}
                            </p>
                            <div className="space-y-1">
                                {group.items.map((menu) => {
                                    const isActive = currentPage === menu.id;
                                    return (
                                        <button 
                                            key={menu.id}
                                            onClick={() => {setCurrentPage(menu.id); setIsSidebarOpen(false)}} 
                                            style={isActive ? {
                                                backgroundColor: `${activeTheme.primaryColor}12`,
                                                color: activeTheme.primaryColor,
                                                borderLeftColor: activeTheme.primaryColor
                                            } : {}}
                                            className={`w-full flex items-center gap-3.5 px-4 py-2.5 rounded-xl font-medium text-xs transition-all duration-200 ${
                                                isActive 
                                                ? 'font-bold shadow-sm border-l-4' 
                                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                            }`}
                                        >
                                            <span 
                                                style={isActive ? { color: activeTheme.primaryColor } : {}}
                                                className={`flex items-center justify-center ${isActive ? '' : 'text-slate-400'}`}
                                            >
                                                {menu.icon}
                                            </span>
                                            <span className="tracking-wide">{menu.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>
                
                {/* ============ MODERN PREMIUM LOGOUT AREA ============ */}
                <div className="mt-auto pt-4 border-t border-slate-200">
                    <div className="p-2.5 rounded-2xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/80 shadow-sm hover:border-slate-300 transition-all group">
                        <div className="flex items-center gap-2.5 mb-2.5">
                            <div 
                                style={{ backgroundColor: activeTheme.primaryColor }}
                                className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-black text-sm shrink-0 shadow-md ring-2 ring-white"
                            >
                                {(currentUsername || 'A').charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-grow min-w-0">
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-none">Signed in as</p>
                                <p className="text-xs font-black text-slate-800 truncate mt-1">{currentUsername}</p>
                            </div>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)] animate-pulse shrink-0"></span>
                        </div>
                        <button 
                            onClick={requestLogout}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-red-300 hover:bg-red-50 text-slate-600 hover:text-red-600 text-[10px] font-black uppercase tracking-widest transition-all duration-200 active:scale-[0.98] shadow-sm hover:shadow"
                            title="Logout dari sistem"
                        >
                            {SidebarIcons.logout}
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </aside>

            {isSidebarOpen && <div onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-slate-900/20 z-40 md:hidden"></div>}

            {/* ===== MAIN CONTENT AREA ===== */}
            <main className={`flex-grow md:ml-64 transition-all duration-300 ${currentPage === 'games' ? 'p-0 bg-transparent' : 'p-4 md:p-8'}`}>
                
                {currentPage !== 'games' && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 max-w-7xl mx-auto bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-semibold text-slate-400">System Path:</span>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-700 capitalize">Console</span>
                                <span className="text-slate-300">/</span>
                                <span 
                                    style={{ color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30` }}
                                    className="text-xs font-extrabold capitalize px-2.5 py-0.5 rounded-md border"
                                >
                                    {currentPage}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full font-bold text-[9px]">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                {isOnline ? 'OPERATIONAL' : 'OFFLINE'}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setCurrentPage('setting')}
                                style={currentPage === 'setting' ? { color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30` } : {}}
                                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${currentPage === 'setting' ? 'shadow-sm' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                            >
                                {SidebarIcons.settings}
                                <span>Setting</span>
                            </button>
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200">
                                <div 
                                    style={{ backgroundColor: activeTheme.primaryColor }}
                                    className="w-6 h-6 rounded-lg flex items-center justify-center text-white font-black text-[10px]"
                                >
                                    {(currentUsername || 'A').charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs font-bold text-slate-700 max-w-[100px] truncate">{currentUsername}</span>
                            </div>
                            <div className="text-right border-l border-slate-200 pl-3">
                                <span className="text-[10px] font-bold text-slate-400 block -mb-0.5">WIB SYSTEM TIME</span>
                                <span className="text-slate-700 font-mono font-bold text-xs">{currentTime.toLocaleTimeString('id-ID')}</span>
                            </div>
                        </div>
                    </div>
                )}

                {isSyncing && currentPage !== 'games' && (
                    <div 
                        style={{ backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30`, color: activeTheme.primaryColor }}
                        className="w-full max-w-7xl mx-auto mb-4 border rounded-xl px-4 py-2 flex items-center justify-between animate-pulse"
                    >
                        <span className="font-bold text-[10px] uppercase tracking-wide">Syncing real-time state with Cloud Firestore...</span>
                        <span className="text-xs">⚡</span>
                    </div>
                )}

                {currentPage === 'dashboard' ? (
                    (() => {
                        // ===== Nilai turunan khusus tampilan dashboard (tidak mengubah data/logika asli) =====
                        const SUPPLIERS = ['DAS', 'AMC', 'HAO', 'NEWBIEZ', 'OASIS', 'ANTUM'];
                        const SUPPLIER_MAX = 5000000;
                        const primary = activeTheme.primaryColor;
                        const accent = activeTheme.accentColor;

                        const chartData = getCombinedTotalChartData();
                        const weekJual = chartData.reduce((s, d) => s + d.Jual, 0);
                        const weekProfit = chartData.reduce((s, d) => s + d.Profit, 0);

                        const margin = totals.jual > 0 ? (totals.profit / totals.jual) * 100 : 0;
                        const rangeMargin = rangeStats.jual > 0 ? (rangeStats.profit / rangeStats.jual) * 100 : 0;
                        const lowCount = SUPPLIERS.filter(n => supplierAlarmSettings[n] !== false && (balances[n] || 0) < lowBalanceThreshold).length;
                        const thresholdPct = Math.min((lowBalanceThreshold / SUPPLIER_MAX) * 100, 100);

                        const isoDate = (d) => d.toISOString().split('T')[0];
                        const todayIso = isoDate(new Date());
                        const activeShortcut = [0, 6, 29].find((days) => {
                            const s = new Date();
                            s.setDate(s.getDate() - days);
                            return startDate === isoDate(s) && endDate === todayIso;
                        });

                        const maxQty = Math.max(1, ...topProducts.map(p => Number(p.qty) || 0));
                        const dateLabel = currentTime.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta' });

                        const sectionHeader = (icon, title, subtitle, right) => (
                            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <span
                                        style={{ color: primary, backgroundColor: `${primary}15`, borderColor: `${primary}30` }}
                                        className="flex h-9 w-9 items-center justify-center rounded-xl border"
                                    >
                                        <DashIcon name={icon} className="h-[18px] w-[18px]" />
                                    </span>
                                    <div>
                                        <h3 className="text-sm font-bold tracking-tight text-slate-800">{title}</h3>
                                        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
                                    </div>
                                </div>
                                {right}
                            </div>
                        );

                        return (
                            <div className="mx-auto max-w-7xl space-y-6 pb-12">

                                {/* --- 1. HEADER RINGKAS: TANGGAL & IMPOR FILE --- */}
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex items-center gap-3">
                                        <span
                                            style={{ color: primary, backgroundColor: `${primary}15`, borderColor: `${primary}30` }}
                                            className="flex h-10 w-10 items-center justify-center rounded-xl border"
                                        >
                                            <DashIcon name="calendar" className="h-5 w-5" />
                                        </span>
                                        <p className="text-sm font-semibold capitalize text-slate-700">{dateLabel}</p>
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage('upload')}
                                        style={{ backgroundColor: primary }}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-[0.98]"
                                    >
                                        <DashIcon name="upload" className="h-4 w-4" /> Impor File Laporan
                                    </button>
                                </div>

                                {/* --- 2. ALERT AREA --- */}
                                {lowBalanceSuppliers.length > 0 && (
                                    <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm sm:flex-row sm:items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-100 text-red-600">
                                                <DashIcon name="alert" className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-red-800">Peringatan: Saldo Supplier Menipis</h4>
                                                <p className="mt-0.5 text-xs leading-relaxed text-red-600">
                                                    {lowBalanceSuppliers.map(s => `${s.name} (${formatRP(s.bal)})`).join(', ')} berada di bawah batas minimum {formatRP(lowBalanceThreshold)}.
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => showToast("Notifikasi deposit alert disiarkan", "success")}
                                            className="shrink-0 rounded-xl bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
                                        >
                                            Tinjau Saldo
                                        </button>
                                    </div>
                                )}

                                {/* --- 3. FINANCIAL SUMMARY CARDS --- */}
                                <div>
                                    <div className="mb-3 flex items-center justify-between px-1">
                                        <h3 className="text-sm font-bold tracking-tight text-slate-800">Kinerja Finansial Akumulatif</h3>
                                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500"></span> Auto-Sync via Firestore
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">

                                        {/* Profit */}
                                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                                            <div style={{ backgroundColor: totals.profit >= 0 ? accent : '#ef4444' }} className="absolute inset-x-0 top-0 h-1"></div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div
                                                    style={{ color: accent, backgroundColor: `${accent}15`, borderColor: `${accent}30` }}
                                                    className="flex h-11 w-11 items-center justify-center rounded-xl border"
                                                >
                                                    <DashIcon name="trendUp" className="h-5 w-5" />
                                                </div>
                                                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${totals.profit >= 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                                                    {totals.profit >= 0 ? 'Positive Margin' : 'Negative Margin'}
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-slate-500">Total Profit Bersih</p>
                                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
                                                <CountUp value={totals.profit} />
                                            </h2>
                                            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400">
                                                <span>Status Perhitungan</span>
                                                <span className="font-semibold text-slate-600">Net Accumulative</span>
                                            </div>
                                        </div>

                                        {/* Omset */}
                                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                                            <div style={{ backgroundColor: primary }} className="absolute inset-x-0 top-0 h-1"></div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div
                                                    style={{ color: primary, backgroundColor: `${primary}15`, borderColor: `${primary}30` }}
                                                    className="flex h-11 w-11 items-center justify-center rounded-xl border"
                                                >
                                                    <DashIcon name="wallet" className="h-5 w-5" />
                                                </div>
                                                <span
                                                    style={{ color: primary, backgroundColor: `${primary}15`, borderColor: `${primary}30` }}
                                                    className="rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                                                >
                                                    Total Volume
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-slate-500">Total Nilai Jual (Omset)</p>
                                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
                                                <CountUp value={totals.jual} />
                                            </h2>
                                            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400">
                                                <span>Gross Revenue</span>
                                                <span className="font-semibold text-slate-600">Terdaftar</span>
                                            </div>
                                        </div>

                                        {/* HPP */}
                                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                                            <div className="absolute inset-x-0 top-0 h-1 bg-slate-400"></div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-slate-600">
                                                    <DashIcon name="inbox" className="h-5 w-5" />
                                                </div>
                                                <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                                    HPP Expense
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-slate-500">Total Harga Modal (HPP)</p>
                                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
                                                <CountUp value={totals.modal} />
                                            </h2>
                                            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400">
                                                <span>Capital Outflow</span>
                                                <span className="font-semibold text-slate-600">Supplier HPP</span>
                                            </div>
                                        </div>

                                        {/* Margin */}
                                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                                            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 to-orange-400"></div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-amber-600">
                                                    <DashIcon name="percent" className="h-5 w-5" />
                                                </div>
                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                                    Rasio Profit
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-slate-500">Margin Keuntungan</p>
                                            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">{margin.toFixed(1)}%</h2>
                                            <div className="mt-4 border-t border-slate-100 pt-3">
                                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                                    <div
                                                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-500"
                                                        style={{ width: `${Math.max(0, Math.min(margin, 100))}%` }}
                                                    ></div>
                                                </div>
                                                <p className="mt-2 text-xs text-slate-400">Profit dibanding total omset</p>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* --- 4. CHART & FILTER --- */}
                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

                                    {/* Main Analytic Chart */}
                                    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
                                        <div className="mb-5 flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
                                            <div>
                                                <h3 className="text-sm font-bold tracking-tight text-slate-800">Performa Transaksi & Profit 7 Hari</h3>
                                                <p className="mt-0.5 text-xs text-slate-400">Metrik perbandingan omset penjualan dan estimasi keuntungan harian.</p>
                                            </div>
                                            <div className="flex items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold">
                                                <div className="flex items-center gap-1.5 text-slate-600">
                                                    <span style={{ backgroundColor: primary }} className="h-2.5 w-2.5 rounded-full"></span> Omset Jual
                                                </div>
                                                <div className="flex items-center gap-1.5 text-slate-600">
                                                    <span style={{ backgroundColor: accent }} className="h-2.5 w-2.5 rounded-full"></span> Profit
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4 grid grid-cols-2 gap-3">
                                            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                                                <p className="text-[11px] font-medium text-slate-500">Omset 7 Hari</p>
                                                <p style={{ color: primary }} className="mt-0.5 font-mono text-sm font-bold tabular-nums">{formatRP(weekJual)}</p>
                                            </div>
                                            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                                                <p className="text-[11px] font-medium text-slate-500">Profit 7 Hari</p>
                                                <p style={weekProfit >= 0 ? { color: accent } : {}} className={`mt-0.5 font-mono text-sm font-bold tabular-nums ${weekProfit < 0 ? 'text-red-600' : ''}`}>{formatRP(weekProfit)}</p>
                                            </div>
                                        </div>

                                        <div className="h-72 w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="dashGradJual" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={primary} stopOpacity={0.28} />
                                                            <stop offset="95%" stopColor={primary} stopOpacity={0} />
                                                        </linearGradient>
                                                        <linearGradient id="dashGradProfit" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={accent} stopOpacity={0.28} />
                                                            <stop offset="95%" stopColor={accent} stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} axisLine={false} tickLine={false} tickMargin={8} />
                                                    <YAxis stroke="#94a3b8" fontSize={11} axisLine={false} tickLine={false} width={48} tickFormatter={formatCompact} />
                                                    <Tooltip content={<DashTooltip />} cursor={{ stroke: '#cbd5e1', strokeDasharray: '4 4' }} />
                                                    <Area type="monotone" dataKey="Jual" stroke={primary} strokeWidth={2.5} fill="url(#dashGradJual)" dot={{ r: 3, fill: primary, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                                                    <Area type="monotone" dataKey="Profit" stroke={accent} strokeWidth={2.5} fill="url(#dashGradProfit)" dot={{ r: 3, fill: accent, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Range Date Filter & Specific Metrics Panel */}
                                    <div className="flex flex-col justify-between gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                                        <div>
                                            {sectionHeader('calendar', 'Filter Rentang Tanggal', 'Analisis periode tertentu')}

                                            <div className="mb-4 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
                                                {[[0, 'Hari Ini'], [6, '7 Hari'], [29, '30 Hari']].map(([days, label]) => (
                                                    <button
                                                        key={days}
                                                        onClick={() => setPeriodShortcut(days)}
                                                        style={activeShortcut === days ? { color: primary } : {}}
                                                        className={`rounded-lg py-2 text-xs font-semibold transition ${activeShortcut === days ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium text-slate-500">Tanggal Mulai</label>
                                                    <input type="date" style={{ color: primary }} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold outline-none transition focus:border-slate-400 focus:bg-white" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium text-slate-500">Tanggal Akhir</label>
                                                    <input type="date" style={{ color: primary }} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold outline-none transition focus:border-slate-400 focus:bg-white" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2.5 rounded-xl border border-slate-100 bg-slate-50 p-4">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="font-medium text-slate-500">Omset Range</span>
                                                <span style={{ color: primary }} className="font-mono font-bold">{formatRP(rangeStats.jual)}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="font-medium text-slate-500">Modal Range</span>
                                                <span className="font-mono font-bold text-slate-700">{formatRP(rangeStats.modal)}</span>
                                            </div>
                                            <div className="flex items-center justify-between border-t border-slate-200 pt-2.5 text-sm">
                                                <span className="font-bold text-slate-700">Profit Range</span>
                                                <span
                                                    style={rangeStats.profit >= 0 ? { color: accent } : {}}
                                                    className={`font-mono font-extrabold ${rangeStats.profit < 0 ? 'text-red-600' : ''}`}
                                                >
                                                    {formatRP(rangeStats.profit)}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-slate-400">
                                                <span>Margin periode</span>
                                                <span className="font-semibold text-slate-600">{rangeMargin.toFixed(1)}%</span>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                {/* --- 5. SUPPLIER MONITORING PANEL --- */}
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                                    {sectionHeader(
                                        'bank',
                                        'Monitoring Saldo & Penggunaan Supplier',
                                        `Threshold alert: ${formatRP(lowBalanceThreshold)}`,
                                        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
                                                <DashIcon name="shield" className="h-3.5 w-3.5" /> {SUPPLIERS.length - lowCount} Aman
                                            </span>
                                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${lowCount > 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
                                                <DashIcon name="alert" className="h-3.5 w-3.5" /> {lowCount} Menipis
                                            </span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                        {SUPPLIERS.map(name => {
                                            const currentBal = balances[name] || 0;
                                            const safePercentage = Math.min((currentBal / SUPPLIER_MAX) * 100, 100);
                                            const isAlarmOn = supplierAlarmSettings[name] !== false;
                                            const isLow = isAlarmOn && currentBal < lowBalanceThreshold;
                                            const delta = currentBal - (yesterdayBalances[name] || 0);

                                            return (
                                                <div key={name} className={`flex flex-col justify-between rounded-2xl border bg-white p-5 transition hover:shadow-md ${isLow ? 'border-red-200 bg-red-50/30' : 'border-slate-200 hover:border-slate-300'}`}>
                                                    <div>
                                                        <div className="mb-4 flex items-start justify-between gap-2">
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                <span
                                                                    style={{ color: primary, backgroundColor: `${primary}15`, borderColor: `${primary}30` }}
                                                                    className="rounded-lg border px-2.5 py-1 text-xs font-bold uppercase tracking-wider"
                                                                >
                                                                    {name}
                                                                </span>
                                                                {isLow ? (
                                                                    <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                                                                        <DashIcon name="alert" className="h-3 w-3" /> Drop
                                                                    </span>
                                                                ) : (
                                                                    <span
                                                                        style={{ color: accent, backgroundColor: `${accent}15`, borderColor: `${accent}30` }}
                                                                        className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold"
                                                                    >
                                                                        <span style={{ backgroundColor: accent }} className="h-1.5 w-1.5 rounded-full"></span> Normal
                                                                    </span>
                                                                )}
                                                                {!isAlarmOn && (
                                                                    <span className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-400">
                                                                        OFF
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-[11px] font-medium text-slate-400">Saldo Kemarin</p>
                                                                <p className="font-mono text-xs font-semibold text-slate-600">
                                                                    {formatRP(yesterdayBalances[name] || 0)}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <p className="text-[11px] font-medium text-slate-400">Saldo Saat Ini</p>
                                                        <h3 className="mt-0.5 font-mono text-xl font-extrabold tracking-tight text-slate-900">
                                                            <CountUp value={balances[name]} />
                                                        </h3>
                                                        <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${delta === 0 ? 'text-slate-400' : delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                            {delta !== 0 && <DashIcon name={delta > 0 ? 'arrowUp' : 'arrowDown'} className="h-3 w-3" />}
                                                            <span className="font-mono">{delta === 0 ? 'Tidak berubah' : `${delta > 0 ? '+' : '-'}${formatRP(Math.abs(delta))}`}</span>
                                                            {delta !== 0 && <span className="font-medium text-slate-400">vs kemarin</span>}
                                                        </div>

                                                        <div className="mt-3 h-12">
                                                            <ResponsiveContainer width="100%" height="100%">
                                                                <AreaChart data={getChartData(name)} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                                                                    <defs>
                                                                        <linearGradient id={`dashSpark-${name}`} x1="0" y1="0" x2="0" y2="1">
                                                                            <stop offset="0%" stopColor={primary} stopOpacity={0.25} />
                                                                            <stop offset="100%" stopColor={primary} stopOpacity={0} />
                                                                        </linearGradient>
                                                                    </defs>
                                                                    <Tooltip
                                                                        contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '8px', color: '#fff', fontSize: '10px' }}
                                                                        itemStyle={{ color: '#fff' }}
                                                                        labelStyle={{ color: '#94a3b8' }}
                                                                        formatter={(value) => [formatRP(value), 'Pemakaian']}
                                                                    />
                                                                    <Area isAnimationActive={false} type="monotone" dataKey="usage" stroke={primary} strokeWidth={2} fill={`url(#dashSpark-${name})`} dot={false} />
                                                                </AreaChart>
                                                            </ResponsiveContainer>
                                                        </div>

                                                        <div className="relative mt-3">
                                                            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                                                <div
                                                                    className="h-full rounded-full transition-all duration-500"
                                                                    style={{
                                                                        width: `${safePercentage}%`,
                                                                        backgroundColor: isLow ? '#ef4444' : primary
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <div
                                                                className="absolute -top-0.5 h-3 w-0.5 rounded bg-slate-400/70"
                                                                style={{ left: `${thresholdPct}%` }}
                                                                title={`Batas minimum ${formatRP(lowBalanceThreshold)}`}
                                                            ></div>
                                                            <div className="mt-1.5 flex justify-between text-[10px] font-medium text-slate-400">
                                                                <span>Rp 0</span>
                                                                <span>Batas {formatCompact(lowBalanceThreshold)}</span>
                                                                <span>{formatCompact(SUPPLIER_MAX)}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
                                                        <span className="font-medium text-slate-400">Pemakaian Hari Ini</span>
                                                        <span className="font-mono font-bold text-red-500">
                                                            -{formatRP(supplierDailyUsage[endDate]?.[name] || 0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* --- 6. TOP PRODUCTS & IMPORT HISTORY --- */}
                                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

                                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 lg:col-span-1">
                                        {sectionHeader('flame', 'Top 5 Produk Terlaris', 'Berdasarkan total volume')}
                                        <div className="space-y-2">
                                            {topProducts.map((prod, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleCopyText(prod.kode)}
                                                    title="Klik untuk menyalin SKU/Kode"
                                                    className="group cursor-pointer rounded-xl border border-slate-100 bg-slate-50 p-3 transition hover:border-slate-200 hover:bg-white hover:shadow-sm"
                                                >
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex min-w-0 items-center gap-3">
                                                            <span
                                                                style={{ backgroundColor: idx === 0 ? primary : '#94a3b8' }}
                                                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                                                            >
                                                                {idx + 1}
                                                            </span>
                                                            <span className="truncate font-mono text-sm font-semibold text-slate-800">{prod.kode}</span>
                                                            <span className="text-slate-300 opacity-0 transition group-hover:opacity-100"><DashIcon name="copy" className="h-3.5 w-3.5" /></span>
                                                        </div>
                                                        <span
                                                            style={{ color: primary, backgroundColor: `${primary}15` }}
                                                            className="shrink-0 rounded-md px-2.5 py-1 text-xs font-bold"
                                                        >
                                                            {prod.qty} Trx
                                                        </span>
                                                    </div>
                                                    <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-200/70">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-500"
                                                            style={{ width: `${((Number(prod.qty) || 0) / maxQty) * 100}%`, backgroundColor: idx === 0 ? primary : '#94a3b8' }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))}
                                            {topProducts.length === 0 && (
                                                <div className="rounded-xl border-2 border-dashed border-slate-200 py-8 text-center">
                                                    <p className="text-sm font-semibold text-slate-500">Belum ada data transaksi</p>
                                                    <p className="mt-0.5 text-xs text-slate-400">Impor laporan untuk melihat produk terlaris.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 lg:col-span-2">
                                        {sectionHeader(
                                            'history',
                                            'Riwayat Log Import Laporan',
                                            `${uploadHistory.length} file tercatat`,
                                            <div className="relative">
                                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><DashIcon name="search" className="h-4 w-4" /></span>
                                                <input
                                                    type="text"
                                                    placeholder="Cari file..."
                                                    value={searchHistory}
                                                    onChange={(e) => setSearchHistory(e.target.value)}
                                                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:bg-white sm:w-56"
                                                />
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            {filteredUploadHistory.slice(0, 5).map((item) => (
                                                <div key={item.id} className="flex flex-col justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3.5 transition hover:border-slate-200 hover:bg-white hover:shadow-sm sm:flex-row sm:items-center">
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-600">
                                                            <DashIcon name="file" className="h-5 w-5" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="break-all font-mono text-sm font-semibold tracking-tight text-slate-800">{item.fileName}</p>
                                                            <p className="mt-0.5 text-xs text-slate-400">{item.uploadTime}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-2 sm:justify-end sm:border-t-0 sm:pt-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                                                Sukses: {item.trxSukses}
                                                            </span>
                                                            <span className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
                                                                Gagal: {item.trxGagal}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => deleteHistoryItem(item.id)}
                                                            title="Hapus riwayat"
                                                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                                        >
                                                            <DashIcon name="trash" className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {filteredUploadHistory.length === 0 && (
                                                <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center">
                                                    <p className="text-sm font-semibold text-slate-500">Belum ada riwayat import file</p>
                                                    <p className="mt-0.5 text-xs text-slate-400">Riwayat akan muncul setelah laporan diimpor.</p>
                                                </div>
                                            )}
                                        </div>
                                        {filteredUploadHistory.length > 5 && (
                                            <p className="mt-3 text-center text-xs text-slate-400">Menampilkan 5 dari {filteredUploadHistory.length} riwayat</p>
                                        )}
                                    </div>

                                </div>

                            </div>
                        );
                    })()
                ) : currentPage === 'rekapan' ? (
                    <RekapanDashboard 
                        externalBalances={balances} 
                        yesterdayBalances={yesterdayBalances}
                        supplierDailyUsage={supplierDailyUsage}
                        filterDate={endDate}
                        webhookId={WEBHOOK_ID}
                        totalProfit={totals.profit} 
                        dailyStats={{
                            totalTrx: (uploadHistory[0]?.trxSukses || 0) + (uploadHistory[0]?.trxGagal || 0),
                            successTrx: uploadHistory[0]?.trxSukses || 0,
                            failedTrx: uploadHistory[0]?.trxGagal || 0,
                            totalSales: dailyData[endDate]?.jual || 0,
                            totalCost: dailyData[endDate]?.modal || 0,
                            successRate: uploadHistory[0]?.trxSukses 
                                ? ((uploadHistory[0].trxSukses / (uploadHistory[0].trxSukses + uploadHistory[0].trxGagal)) * 100).toFixed(1) 
                                : 0
                        }}
                    />
                ) : currentPage === 'validator' ? (
                    <Validator />
                ) : currentPage === 'broadcast' ? (
                    <Broadcast />
                ) : currentPage === 'agen' ? (
                    <DaftarAgen />
                ) : currentPage === 'v-telpon' ? (
                    <ValidasiTelpon />
                ) : currentPage === 'games' ? (
                    <GamesPanel />
                ) : currentPage === 'telegram' ? (
                    <TelegramUpload />
                ) : currentPage === 'speed' ? (
                    <LogSpeedPanel /> 
                ) : currentPage === 'setting' ? (
                    /* HALAMAN SETTING SYSTEM — REDESIGN */
                    <div 
                        className="settings-page max-w-6xl mx-auto pb-16"
                        style={{ '--settings-primary': activeTheme.primaryColor, '--settings-primary-soft': `${activeTheme.primaryColor}14`, '--settings-accent': activeTheme.accentColor }}
                    >
                        <style>{`
                            @keyframes settingsFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                            @keyframes settingsPanelIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
                            @keyframes toastIn { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
                            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                            @keyframes popIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
                            .settings-page { animation: settingsFadeIn .45s cubic-bezier(0.16, 1, 0.3, 1); }
                            .settings-panel { display: none; }
                            .settings-nav-icon { transition: background-color .2s ease, color .2s ease, box-shadow .2s ease; }
                            .settings-nav-item { transition: background-color .2s ease, color .2s ease, transform .2s ease; }
                            .settings-nav-item:hover { transform: translateX(2px); }
                            .settings-nav-sub { transition: color .2s ease, opacity .2s ease; }
                            .settings-card { transition: box-shadow .3s ease, border-color .3s ease, transform .3s ease; }
                            .settings-card:hover { box-shadow: 0 12px 28px -12px rgba(15, 23, 42, 0.14); }
                            .settings-input, .settings-btn { transition: all .2s ease; }
                            .settings-btn:active { transform: scale(0.97); }
                            .settings-toggle-track { transition: background-color .25s ease; }
                            .settings-toggle-knob { transition: transform .25s cubic-bezier(0.34, 1.56, 0.64, 1); }
                            .settings-supplier-card { transition: border-color .2s ease, background-color .2s ease, transform .15s ease; }
                            .settings-supplier-card:hover { transform: translateY(-1px); }

                            #tab-profile:checked ~ .settings-layout #panel-profile,
                            #tab-branding:checked ~ .settings-layout #panel-branding,
                            #tab-appearance:checked ~ .settings-layout #panel-appearance,
                            #tab-alarm:checked ~ .settings-layout #panel-alarm,
                            #tab-logo:checked ~ .settings-layout #panel-logo,
                            #tab-danger:checked ~ .settings-layout #panel-danger {
                                display: block;
                                animation: settingsPanelIn .35s cubic-bezier(0.16, 1, 0.3, 1);
                            }

                            #tab-profile:checked ~ .settings-layout #nav-profile,
                            #tab-branding:checked ~ .settings-layout #nav-branding,
                            #tab-appearance:checked ~ .settings-layout #nav-appearance,
                            #tab-alarm:checked ~ .settings-layout #nav-alarm,
                            #tab-logo:checked ~ .settings-layout #nav-logo {
                                background-color: var(--settings-primary-soft);
                                color: var(--settings-primary);
                            }
                            #tab-profile:checked ~ .settings-layout #nav-profile .settings-nav-icon,
                            #tab-branding:checked ~ .settings-layout #nav-branding .settings-nav-icon,
                            #tab-appearance:checked ~ .settings-layout #nav-appearance .settings-nav-icon,
                            #tab-alarm:checked ~ .settings-layout #nav-alarm .settings-nav-icon,
                            #tab-logo:checked ~ .settings-layout #nav-logo .settings-nav-icon {
                                background-color: #ffffff;
                                color: var(--settings-primary);
                                box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
                            }
                            #tab-profile:checked ~ .settings-layout #nav-profile .settings-nav-sub,
                            #tab-branding:checked ~ .settings-layout #nav-branding .settings-nav-sub,
                            #tab-appearance:checked ~ .settings-layout #nav-appearance .settings-nav-sub,
                            #tab-alarm:checked ~ .settings-layout #nav-alarm .settings-nav-sub,
                            #tab-logo:checked ~ .settings-layout #nav-logo .settings-nav-sub {
                                color: var(--settings-primary);
                                opacity: 0.75;
                            }
                            #tab-danger:checked ~ .settings-layout #nav-danger {
                                background-color: #fef2f2;
                                color: #dc2626;
                            }
                            #tab-danger:checked ~ .settings-layout #nav-danger .settings-nav-icon {
                                background-color: #ffffff;
                                color: #dc2626;
                                box-shadow: 0 1px 3px rgba(220, 38, 38, 0.15);
                            }
                            #tab-danger:checked ~ .settings-layout #nav-danger .settings-nav-sub {
                                color: #dc2626;
                                opacity: 0.75;
                            }
                        `}</style>

                        {/* ============ HEADER ============ */}
                        <div className="bg-white p-6 md:p-7 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                            <div>
                                <div className="flex items-center gap-3 mb-1.5">
                                    <span 
                                        style={{ color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30` }}
                                        className="p-2.5 rounded-xl border"
                                    >
                                        {SidebarIcons.settings}
                                    </span>
                                    <div>
                                        <h1 className="text-lg md:text-xl font-black text-slate-800 tracking-tight leading-tight">Pengaturan Sistem</h1>
                                        <p className="text-xs text-slate-400 leading-relaxed mt-0.5">Kelola profil admin, identitas aplikasi, tema tampilan, notifikasi alarm, logo, dan pemeliharaan database.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl shrink-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Versi</span>
                                <span className="text-xs font-black text-slate-800 font-mono">v{APP_VERSION}</span>
                            </div>
                        </div>

                        {/* ============ CSS-DRIVEN TAB STATE ============ */}
                        <input type="radio" id="tab-profile" name="settingsTabs" defaultChecked className="hidden" />
                        <input type="radio" id="tab-branding" name="settingsTabs" className="hidden" />
                        <input type="radio" id="tab-appearance" name="settingsTabs" className="hidden" />
                        <input type="radio" id="tab-alarm" name="settingsTabs" className="hidden" />
                        <input type="radio" id="tab-logo" name="settingsTabs" className="hidden" />
                        <input type="radio" id="tab-danger" name="settingsTabs" className="hidden" />

                        <div className="settings-layout flex flex-col lg:flex-row gap-6 items-start">

                            {/* ============ NAV / TAB LIST ============ */}
                            <nav className="w-full lg:w-64 shrink-0 lg:sticky lg:top-6">
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">

                                    <label htmlFor="tab-profile" id="nav-profile" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0 lg:shrink lg:w-full whitespace-nowrap">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            {SidebarIcons.profile}
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Profil Admin</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Username & password</span>
                                        </span>
                                    </label>

                                    <label htmlFor="tab-branding" id="nav-branding" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0 lg:shrink lg:w-full whitespace-nowrap">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Branding</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Nama & subtitle</span>
                                        </span>
                                    </label>

                                    <label htmlFor="tab-appearance" id="nav-appearance" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0 lg:shrink lg:w-full whitespace-nowrap">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="8" cy="8" r="2.5" strokeWidth="2" />
                                                <circle cx="16" cy="8" r="2.5" strokeWidth="2" />
                                                <circle cx="12" cy="16" r="2.5" strokeWidth="2" />
                                            </svg>
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Tampilan</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Tema & warna</span>
                                        </span>
                                    </label>

                                    <label htmlFor="tab-alarm" id="nav-alarm" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0 lg:shrink lg:w-full whitespace-nowrap">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Notifikasi</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Alarm saldo</span>
                                        </span>
                                    </label>

                                    <label htmlFor="tab-logo" id="nav-logo" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-slate-50 hover:text-slate-700 shrink-0 lg:shrink lg:w-full whitespace-nowrap">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Logo</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Identitas visual</span>
                                        </span>
                                    </label>

                                    <label htmlFor="tab-danger" id="nav-danger" className="settings-nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer text-slate-500 hover:bg-red-50 hover:text-red-600 shrink-0 lg:shrink lg:w-full whitespace-nowrap lg:mt-2 lg:pt-3 lg:border-t lg:border-slate-100">
                                        <span className="settings-nav-icon w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </span>
                                        <span className="text-left">
                                            <span className="block text-xs font-bold">Database</span>
                                            <span className="settings-nav-sub hidden lg:block text-[10px] text-slate-400 font-medium">Zona berbahaya</span>
                                        </span>
                                    </label>
                                </div>
                            </nav>

                            {/* ============ CONTENT / PANELS ============ */}
                            <div className="settings-body flex-1 min-w-0 w-full">

                                {/* ---------- PANEL: PROFIL ADMIN ---------- */}
                                <div id="panel-profile" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-slate-800">Profil Administrator</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Kelola kredensial login Anda. Perubahan username akan langsung berlaku sebagai akun login utama.</p>
                                    </div>

                                    {/* CARD: USERNAME */}
                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7 mb-5">
                                        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                                            <div 
                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-base shadow-md ring-2 ring-white"
                                            >
                                                {(currentUsername || 'A').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Akun Aktif</p>
                                                <p className="text-sm font-black text-slate-800 truncate">{currentUsername}</p>
                                            </div>
                                        </div>

                                        {profileError && (
                                            <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <span>{profileError}</span>
                                            </div>
                                        )}

                                        <label className="text-xs font-bold text-slate-800 block mb-2">Ubah Username Login</label>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <input 
                                                type="text"
                                                value={profileUsernameInput || currentUsername}
                                                onChange={(e) => setProfileUsernameInput(e.target.value)}
                                                placeholder="Masukkan username baru"
                                                maxLength={30}
                                                className="settings-input flex-grow bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                                            />
                                            <button 
                                                type="button"
                                                onClick={handleSaveUsername}
                                                disabled={isSavingProfile}
                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                className="settings-btn text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-60"
                                            >
                                                {isSavingProfile ? 'Menyimpan...' : 'Simpan Username'}
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-2">Gunakan 3–30 karakter (huruf, angka, titik, underscore, strip).</p>
                                    </div>

                                    {/* CARD: PASSWORD */}
                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7">
                                        <div className="mb-4 pb-4 border-b border-slate-100">
                                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">Ubah Password</h3>
                                            <p className="text-[11px] text-slate-400 mt-1">Password disimpan dalam bentuk hash SHA-256 di Firestore.</p>
                                        </div>

                                        {passwordError && (
                                            <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <span>{passwordError}</span>
                                            </div>
                                        )}
                                        {passwordSuccess && (
                                            <div className="p-3 mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>{passwordSuccess}</span>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Password Saat Ini</label>
                                                <input 
                                                    type="password"
                                                    value={currentPasswordInput}
                                                    onChange={(e) => setCurrentPasswordInput(e.target.value)}
                                                    placeholder="••••••••"
                                                    className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-medium text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Password Baru</label>
                                                <input 
                                                    type="password"
                                                    value={newPasswordInput}
                                                    onChange={(e) => setNewPasswordInput(e.target.value)}
                                                    placeholder="Min. 6 karakter"
                                                    className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-medium text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Konfirmasi Password Baru</label>
                                                <input 
                                                    type="password"
                                                    value={confirmPasswordInput}
                                                    onChange={(e) => setConfirmPasswordInput(e.target.value)}
                                                    placeholder="Ulangi password baru"
                                                    className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-medium text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                                                />
                                            </div>
                                        </div>

                                        <div className="pt-5 mt-5 border-t border-slate-100 flex justify-end">
                                            <button 
                                                type="button"
                                                onClick={handleChangePassword}
                                                disabled={isSavingPassword}
                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                className="settings-btn text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center gap-2 disabled:opacity-60"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <span>{isSavingPassword ? 'Menyimpan...' : 'Perbarui Password'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* ---------- PANEL: BRANDING ---------- */}
                                <div id="panel-branding" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-slate-800">Nama Aplikasi & Branding</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Nama dan subtitle ini diterapkan di login, sidebar, header, dan seluruh tampilan yang menggunakan identitas sistem.</p>
                                    </div>

                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7 space-y-5">
                                        {/* NAMA UTAMA */}
                                        <div>
                                            <label className="text-xs font-bold text-slate-800 block mb-2">Nama Aplikasi / Brand Utama</label>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <input 
                                                    type="text" 
                                                    value={appTitleInput} 
                                                    onChange={(e) => setAppTitleInput(e.target.value)}
                                                    placeholder="Ganti nama aplikasi (default: IFYONE)" 
                                                    maxLength={32}
                                                    className="settings-input flex-grow bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 uppercase"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={handleSaveAppName}
                                                    style={{ backgroundColor: activeTheme.primaryColor }}
                                                    className="settings-btn text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center justify-center gap-1.5 shrink-0"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Simpan Nama
                                                </button>
                                            </div>
                                            <div className="mt-2 flex justify-between items-center text-[10px]">
                                                <span className="text-slate-400">Default: <strong className="text-slate-600">IFYONE</strong></span>
                                                <button 
                                                    type="button" 
                                                    onClick={handleResetAppName}
                                                    className="settings-btn text-red-500 hover:text-red-700 font-bold inline-flex items-center gap-1"
                                                >
                                                    Reset Nama
                                                </button>
                                            </div>
                                        </div>

                                        {/* SUBTITLE */}
                                        <div className="pt-5 border-t border-slate-100">
                                            <label className="text-xs font-bold text-slate-800 block mb-2">Subtitle / Tagline (di bawah brand)</label>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <input 
                                                    type="text" 
                                                    value={appSubtitleInput} 
                                                    onChange={(e) => setAppSubtitleInput(e.target.value)}
                                                    placeholder="Contoh: Admin Panel" 
                                                    maxLength={40}
                                                    className="settings-input flex-grow bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={handleSaveAppSubtitle}
                                                    style={{ backgroundColor: activeTheme.primaryColor }}
                                                    className="settings-btn text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center justify-center gap-1.5 shrink-0"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Simpan Subtitle
                                                </button>
                                            </div>
                                            <div className="mt-2 flex justify-between items-center text-[10px]">
                                                <span className="text-slate-400">Default: <strong className="text-slate-600">Admin Panel</strong></span>
                                                <button 
                                                    type="button" 
                                                    onClick={handleResetAppSubtitle}
                                                    className="settings-btn text-red-500 hover:text-red-700 font-bold inline-flex items-center gap-1"
                                                >
                                                    Reset Subtitle
                                                </button>
                                            </div>
                                        </div>

                                        {/* PREVIEW */}
                                        <div className="pt-5 border-t border-slate-100">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Preview Identitas</span>
                                            <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                                                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center p-1.5 shrink-0 shadow-sm">
                                                    <img 
                                                        src={currentLogo} 
                                                        alt="Logo" 
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_LOGO; }}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-sm font-black tracking-tight text-slate-800 uppercase truncate">{appTitleInput || DEFAULT_APP_TITLE}</h3>
                                                    <p className="text-[10px] font-bold text-slate-400 tracking-wider uppercase truncate">{appSubtitleInput || DEFAULT_APP_SUBTITLE}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ---------- PANEL: TAMPILAN / TEMA WARNA ---------- */}
                                <div id="panel-appearance" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-slate-800">Tema & Warna Tampilan</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Atur warna utama dan aksen yang digunakan di seluruh komponen antarmuka aplikasi.</p>
                                    </div>

                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7 space-y-7">
                                        {colorError && (
                                            <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <span>{colorError}</span>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                                            <div className="space-y-6">
                                                {/* WARNA UTAMA */}
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-slate-800 block">Warna Utama</label>
                                                    <p className="text-[11px] text-slate-400 leading-relaxed">Digunakan untuk elemen utama dashboard, tombol primary, navigasi, dan komponen UI terkait.</p>

                                                    <div className="flex items-center gap-3 pt-1">
                                                        <div className="relative flex items-center justify-center w-10 h-10 rounded-xl border border-slate-300 overflow-hidden shrink-0 shadow-sm">
                                                            <input 
                                                                type="color" 
                                                                value={isValidHexColor(primaryInput) ? primaryInput : DEFAULT_PRIMARY_COLOR} 
                                                                onChange={(e) => setPrimaryInput(e.target.value.toUpperCase())}
                                                                className="absolute inset-0 w-16 h-16 -top-3 -left-3 cursor-pointer opacity-0" 
                                                            />
                                                            <div 
                                                                style={{ backgroundColor: isValidHexColor(primaryInput) ? primaryInput : '#cbd5e1' }} 
                                                                className="w-full h-full"
                                                            ></div>
                                                        </div>

                                                        <div className="relative flex-grow">
                                                            <input 
                                                                type="text" 
                                                                value={primaryInput} 
                                                                maxLength={7}
                                                                onChange={(e) => setPrimaryInput(e.target.value.toUpperCase())}
                                                                placeholder="#4F46E5" 
                                                                className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-mono font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 uppercase"
                                                            />
                                                        </div>

                                                        <div 
                                                            title="Preview Warna Utama"
                                                            style={{ backgroundColor: isValidHexColor(primaryInput) ? primaryInput : '#cbd5e1' }}
                                                            className="w-10 h-10 rounded-xl border border-slate-200 shrink-0 shadow-sm"
                                                        ></div>
                                                    </div>
                                                </div>

                                                {/* WARNA AKSEN */}
                                                <div className="space-y-2 pt-2 border-t border-slate-100">
                                                    <label className="text-xs font-bold text-slate-800 block">Warna Aksen</label>
                                                    <p className="text-[11px] text-slate-400 leading-relaxed">Digunakan untuk elemen pendukung, icon tertentu, indikator status, highlight, dan border khusus.</p>

                                                    <div className="flex items-center gap-3 pt-1">
                                                        <div className="relative flex items-center justify-center w-10 h-10 rounded-xl border border-slate-300 overflow-hidden shrink-0 shadow-sm">
                                                            <input 
                                                                type="color" 
                                                                value={isValidHexColor(accentInput) ? accentInput : DEFAULT_ACCENT_COLOR} 
                                                                onChange={(e) => setAccentInput(e.target.value.toUpperCase())}
                                                                className="absolute inset-0 w-16 h-16 -top-3 -left-3 cursor-pointer opacity-0" 
                                                            />
                                                            <div 
                                                                style={{ backgroundColor: isValidHexColor(accentInput) ? accentInput : '#cbd5e1' }} 
                                                                className="w-full h-full"
                                                            ></div>
                                                        </div>

                                                        <div className="relative flex-grow">
                                                            <input 
                                                                type="text" 
                                                                value={accentInput} 
                                                                maxLength={7}
                                                                onChange={(e) => setAccentInput(e.target.value.toUpperCase())}
                                                                placeholder="#10B981" 
                                                                className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-3.5 text-xs font-mono font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 uppercase"
                                                            />
                                                        </div>

                                                        <div 
                                                            title="Preview Warna Aksen"
                                                            style={{ backgroundColor: isValidHexColor(accentInput) ? accentInput : '#cbd5e1' }}
                                                            className="w-10 h-10 rounded-xl border border-slate-200 shrink-0 shadow-sm"
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* PREVIEW */}
                                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                                                <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Preview Tampilan Komponen</span>
                                                    <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Live Preview</span>
                                                </div>

                                                <div className="space-y-3">
                                                    <div>
                                                        <span className="text-[10px] font-semibold text-slate-400 block mb-1">Tombol Utama (Primary Button)</span>
                                                        <button 
                                                            type="button"
                                                            style={{ backgroundColor: activeTheme.primaryColor }}
                                                            className="settings-btn w-full text-white font-bold py-2.5 px-4 rounded-xl text-xs shadow-md"
                                                        >
                                                            Simpan Pengaturan
                                                        </button>
                                                    </div>

                                                    <div>
                                                        <span className="text-[10px] font-semibold text-slate-400 block mb-1">Badge & Highlight Utama</span>
                                                        <div className="flex items-center gap-2">
                                                            <span 
                                                                style={{ color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15`, borderColor: `${activeTheme.primaryColor}30` }}
                                                                className="px-3 py-1 rounded-lg border font-extrabold text-[10px]"
                                                            >
                                                                ● Active Status
                                                            </span>
                                                            <span 
                                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                                className="text-white px-2.5 py-1 rounded-md font-bold text-[10px]"
                                                            >
                                                                Tag Utama
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="pt-2 border-t border-slate-200">
                                                        <span className="text-[10px] font-semibold text-slate-400 block mb-1">Indikator & Highlight Aksen</span>
                                                        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200">
                                                            <div className="flex items-center gap-2">
                                                                <span style={{ color: activeTheme.accentColor }} className="shrink-0">
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                                    </svg>
                                                                </span>
                                                                <span className="text-xs font-bold text-slate-700">Margin Keuntungan</span>
                                                            </div>
                                                            <span 
                                                                style={{ color: activeTheme.accentColor, backgroundColor: `${activeTheme.accentColor}15`, borderColor: `${activeTheme.accentColor}30` }}
                                                                className="px-2.5 py-0.5 rounded-full text-[9px] font-extrabold border"
                                                            >
                                                                +24.8%
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        <div className="pt-5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                                            <button 
                                                type="button"
                                                onClick={handleResetColors}
                                                className="settings-btn text-red-500 hover:text-red-700 font-bold text-xs inline-flex items-center gap-1.5"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Reset ke Warna Default
                                            </button>

                                            <button 
                                                type="button"
                                                onClick={handleSaveColors}
                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                className="settings-btn text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center gap-2"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Simpan Pengaturan Warna
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* ---------- PANEL: ALARM / NOTIFIKASI ---------- */}
                                <div id="panel-alarm" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-slate-800">Alarm & Pengecualian Saldo Supplier</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Atur notifikasi Telegram saat saldo supplier berada di bawah batas minimum.</p>
                                    </div>

                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7 space-y-6">
                                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-800">Status Master Alarm Telegram</h4>
                                                <p className="text-[11px] text-slate-400 mt-0.5">Aktifkan atau nonaktifkan sistem alarm peringatan saldo secara menyeluruh.</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    checked={alarmEnabled} 
                                                    onChange={(e) => setAlarmEnabled(e.target.checked)} 
                                                    className="sr-only peer" 
                                                />
                                                <div 
                                                    style={{ backgroundColor: alarmEnabled ? activeTheme.primaryColor : undefined }}
                                                    className="settings-toggle-track w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer"
                                                >
                                                    <div 
                                                        style={{ transform: alarmEnabled ? 'translateX(20px)' : 'translateX(2px)' }}
                                                        className="settings-toggle-knob absolute top-[2px] bg-white border border-slate-300 rounded-full h-5 w-5"
                                                    ></div>
                                                </div>
                                            </label>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                                            <div>
                                                <label className="text-xs font-bold text-slate-800 block mb-1">Batas Minimum Saldo (Rp)</label>
                                                <p className="text-[11px] text-slate-400 mb-2">Jika saldo supplier berada di bawah angka ini, peringatan akan dikirim ke Telegram (selama alarm supplier aktif).</p>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-2.5 font-bold text-slate-400 text-xs">Rp</span>
                                                    <input 
                                                        type="number" 
                                                        value={lowBalanceThreshold} 
                                                        onChange={(e) => setLowBalanceThreshold(e.target.value)} 
                                                        className="settings-input w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-9 pr-4 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10" 
                                                        placeholder="500000"
                                                    />
                                                </div>
                                            </div>
                                            <div 
                                                style={{ backgroundColor: `${activeTheme.primaryColor}08`, borderColor: `${activeTheme.primaryColor}20` }}
                                                className="p-4 rounded-xl border text-[11px] text-slate-700 leading-relaxed flex items-start gap-2.5"
                                            >
                                                <svg style={{ color: activeTheme.primaryColor }} className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span><span className="font-bold">Aturan Pengecualian:</span> Jika alarm supplier diset ke <span className="font-semibold text-red-600">OFF</span> (misal HAO), maka sistem tidak akan memberikan notifikasi alarm saldo untuk supplier tersebut meskipun saldonya di bawah limit.</span>
                                            </div>
                                        </div>

                                        <div className="pt-5 border-t border-slate-100">
                                            <h4 className="text-xs font-bold text-slate-800 mb-1">Atur Alarm Per Supplier (ON/OFF)</h4>
                                            <p className="text-[11px] text-slate-400 mb-4">Matikan tombol jika tidak ingin menerima notifikasi alarm saldo untuk supplier tertentu.</p>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {['DAS', 'AMC', 'HAO', 'NEWBIEZ', 'OASIS', 'ANTUM'].map(supplierKey => {
                                                    const isON = supplierAlarmSettings[supplierKey] !== false;
                                                    return (
                                                        <div 
                                                            key={supplierKey}
                                                            className="settings-supplier-card flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50/70 hover:bg-slate-50"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-extrabold text-xs text-slate-800 font-mono">{supplierKey}</span>
                                                                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${isON ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                                    {isON ? 'Alarm ON' : 'Alarm OFF'}
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleSupplierAlarm(supplierKey)}
                                                                style={{ backgroundColor: isON ? activeTheme.primaryColor : '#cbd5e1' }}
                                                                className="settings-btn px-3 py-1 rounded-lg text-white font-extrabold text-[10px] uppercase tracking-wider shadow-sm"
                                                            >
                                                                {isON ? 'ON' : 'OFF'}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-slate-100 flex justify-end">
                                            <button 
                                                onClick={handleSaveAlarmSettings}
                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                className="settings-btn text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center gap-2"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Simpan Pengaturan Alarm
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* ---------- PANEL: LOGO ---------- */}
                                <div id="panel-logo" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-slate-800">Logo / Icon {appTitle}</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Unggah logo baru untuk memperbarui identitas visual aplikasi di seluruh tampilan.</p>
                                    </div>

                                    <div className="settings-card bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                                            <div className="flex flex-col items-center justify-center p-6 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-center relative group">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4">
                                                    {previewLogo ? "Pratinjau Logo Baru" : "Logo Saat Ini"}
                                                </span>

                                                <div className="w-32 h-32 rounded-2xl bg-white border border-slate-200/80 p-3 flex items-center justify-center shadow-md relative overflow-hidden group-hover:scale-105 transition-transform duration-300">
                                                    <img 
                                                        src={previewLogo || currentLogo} 
                                                        alt={`${appTitle} Logo Preview`} 
                                                        className="w-full h-full object-contain filter drop-shadow-sm"
                                                        onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_LOGO; }}
                                                    />
                                                </div>

                                                <p className="text-[11px] font-semibold text-slate-500 mt-4">
                                                    {selectedFile ? selectedFile.name : (previewLogo ? "Siap disimpan" : "Menggunakan logo aktif")}
                                                </p>
                                            </div>

                                            <div className="space-y-5">
                                                <div>
                                                    <h4 className="text-xs font-bold text-slate-800 mb-1">Unggah Berkas Logo Baru</h4>
                                                    <p className="text-[11px] text-slate-400 leading-relaxed">
                                                        Format yang didukung: <span className="font-semibold text-slate-600">PNG, JPG, WebP, SVG</span>. 
                                                        Disarankan menggunakan format <span className="font-semibold text-slate-600">PNG Transparan</span> dengan rasio 1:1. Maksimal ukuran file 2MB.
                                                    </p>
                                                </div>

                                                {logoError && (
                                                    <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[11px] font-bold flex items-center gap-2">
                                                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                        </svg>
                                                        <span>{logoError}</span>
                                                    </div>
                                                )}

                                                <div className="space-y-3 pt-2">
                                                    <input 
                                                        type="file" 
                                                        accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml" 
                                                        id="logoUploadInput" 
                                                        className="hidden" 
                                                        onChange={handleLogoSelect}
                                                    />

                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <label 
                                                            htmlFor="logoUploadInput" 
                                                            className="settings-btn bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-xl font-bold text-xs cursor-pointer inline-flex items-center gap-2"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                            </svg>
                                                            Pilih File Logo
                                                        </label>

                                                        {previewLogo && (
                                                            <button 
                                                                onClick={handleSaveLogo}
                                                                disabled={isUploadingLogo}
                                                                style={{ backgroundColor: activeTheme.primaryColor }}
                                                                className="settings-btn text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:opacity-90 hover:shadow-lg inline-flex items-center gap-2 disabled:opacity-50"
                                                            >
                                                                {isUploadingLogo ? (
                                                                    <span>Sedang Menyimpan...</span>
                                                                ) : (
                                                                    <>
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        <span>Simpan Logo</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="pt-4 border-t border-slate-100">
                                                        <button 
                                                            onClick={handleResetLogo}
                                                            disabled={isUploadingLogo}
                                                            className="settings-btn text-red-500 hover:text-red-700 font-bold text-xs inline-flex items-center gap-1.5"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                            </svg>
                                                            Reset ke Logo Default
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                {/* ---------- PANEL: DANGER ZONE / DATABASE ---------- */}
                                <div id="panel-danger" className="settings-panel">
                                    <div className="mb-4">
                                        <h2 className="text-sm font-bold text-red-700">Pemeliharaan & Reset Database</h2>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">Tindakan pada zona ini bersifat permanen dan tidak dapat dibatalkan. Lakukan dengan hati-hati.</p>
                                    </div>

                                    <div className="settings-card bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-3.5 bg-red-50/60 border-b border-red-100 flex items-center gap-2">
                                            <svg className="w-3.5 h-3.5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <span className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Danger Zone — Tindakan Permanen</span>
                                        </div>

                                        <div className="p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-800">Reset Seluruh Database Cloud</h4>
                                                <p className="text-[11px] text-slate-400 mt-1 max-w-xl leading-relaxed">
                                                    Tindakan ini akan mengosongkan seluruh riwayat laporan, saldo supplier, catatan transaksi harian, serta log speed di Firebase secara permanen. Pastikan Anda telah membuat cadangan sebelum melakukan reset.
                                                </p>
                                            </div>
                                            <button 
                                                onClick={handleReset} 
                                                className="settings-btn bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md shadow-red-600/10 hover:shadow-lg inline-flex items-center gap-2 shrink-0"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                <span>Reset Database</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* INFO VERSI */}
                                    <div className="mt-5 p-5 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                                        <div>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Informasi Aplikasi</p>
                                            <p className="text-xs font-black text-slate-700 mt-0.5">{appTitle} <span className="text-slate-400 font-semibold">— {appSubtitle}</span></p>
                                        </div>
                                        <span className="text-[11px] font-black text-slate-500 font-mono bg-white px-3 py-1.5 rounded-xl border border-slate-200">v{APP_VERSION}</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ) : (
                    /* Halaman Upload Laporan / Import */
                    <div className="max-w-4xl mx-auto space-y-6">
                        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm text-center border-2 border-dashed border-slate-200 bg-gradient-to-b from-white to-slate-50/30">
                             <div 
                                style={{ color: activeTheme.primaryColor, backgroundColor: `${activeTheme.primaryColor}15` }}
                                className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl mx-auto mb-5 shadow-inner"
                            >
                                📤
                             </div>
                             <h2 className="text-sm font-black mb-1.5 uppercase text-slate-800 tracking-wide">Import Data ke Cloud</h2>
                             <p className="text-slate-400 text-xs mb-6 max-w-sm mx-auto">Silakan pilih file laporan berformat Excel (.xlsx, .xls) atau .csv untuk sinkronisasi database harian.</p>
                             <input type="file" accept=".xlsx, .xls, .csv" onChange={(e) => processFile(e.target.files[0])} className="hidden" id="fUp" />
                             <label 
                                htmlFor="fUp" 
                                style={{ backgroundColor: activeTheme.primaryColor }}
                                className="inline-block text-white px-8 py-3 rounded-xl font-bold cursor-pointer shadow-md hover:opacity-90 active:scale-[0.98] transition-all text-xs uppercase tracking-wider"
                            >
                                Pilih Dokumen Excel
                             </label>
                        </div>

                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-base">📜</span>
                                    <h3 className="text-xs font-bold uppercase text-slate-800 tracking-wider">Riwayat Upload Terakhir</h3>
                                </div>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        placeholder="Cari nama log file..." 
                                        value={searchHistory}
                                        onChange={(e) => setSearchHistory(e.target.value)}
                                        className="bg-slate-50 border border-slate-200 px-3 py-1 rounded-xl font-semibold text-xs outline-none w-full sm:w-48 pl-7 text-slate-700"
                                    />
                                    <span className="absolute left-2.5 top-1.5 opacity-40">🔍</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {filteredUploadHistory.map((item) => (
                                    <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl gap-3 hover:bg-slate-100/60 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center font-black text-xs shrink-0">
                                                XLS
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-slate-800 font-mono tracking-tight break-all">{item.fileName}</p>
                                                <p className="text-[9px] font-medium text-slate-400 uppercase">{item.uploadTime}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-200">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg">
                                                    Sukses: {item.trxSukses}
                                                </span>
                                                <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-lg">
                                                    Gagal: {item.trxGagal}
                                                </span>
                                            </div>
                                            <button onClick={() => deleteHistoryItem(item.id)} className="text-red-400 hover:text-red-600 font-bold text-xs p-1">
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {filteredUploadHistory.length === 0 && (
                                    <p className="text-slate-400 text-center py-6 text-xs">Belum ada riwayat import file.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </main>
        </div>
    );
}

export default App;